/**
 * Properties menu: clickable list of B2B, User or Event properties and cohorts to go inside mp-drop-menu
 */
import compact from 'lodash/compact';
import cuid from 'cuid';
import debounce from 'lodash/debounce';
import flatten from 'lodash/flatten';
import intersectionBy from 'lodash/intersectionBy';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import omit from 'lodash/omit';
import partition from 'lodash/partition';
import pick from 'lodash/pick';
import sortBy from 'lodash/sortBy';
import uniq from 'lodash/uniq';
import keyBy from 'lodash/keyBy';
import {AttrsSchema} from 'panel';
import isNil from 'lodash/isNil';
import pickBy from 'lodash/pickBy';

import {
  DISABLED_DUE_TO_CLASSIFIED_DATA,
  MappedPropertyValues,
  Permission,
  ProjectFeatureFlag,
  SPECIAL_EVENTS,
  STRING_TYPECAST_PROPERTIES,
  TYPECAST_PROPERTY_TYPES,
} from 'common/report/constants';
import type {InputComponent} from 'common/components/input';
import type {TagInput} from 'common/components/tag-input';
import {eventPropertyDefinitionsStore, peoplePropertyDefinitionsStore} from 'common/report/stores/property-definitions';
import {userDetailsClient} from 'common/report/queries/app-api/user-details-client';
import {userDetailsStore} from 'common/report/stores/user-details';
import {dataGroupIDsEqual, replaceByIndex} from 'common/util';
import {Cohort as CohortModel} from 'common/widgets/cohorts-query-builder/models/cohort';
import {
  isSerializedInlineCohort,
  SerializedCohort,
  SerializedInlineCohort,
  User,
} from 'common/widgets/cohorts-query-builder/models/types/serialized-cohort-types';
import {promptCustomProperties} from 'common/widgets/custom-property-modal';
import {promptInlineCohorts} from 'common/widgets/inline-cohorts-modal/util';
import {INSIGHTS_PROPERTY_FILTER_RESOURCE_TYPE_MAPPING} from 'common/widgets/property-filter-menu/models/insights-filter';
import {eventPropertiesClient} from 'common/report/queries/query-api/event-properties-client';
import {peoplePropertiesClient} from 'common/report/queries/query-api/people-properties-client';
import {ProcessedArbProperty} from 'common/report/queries/query-api/utils';
import {cohortsStore} from 'common/report/stores/cohorts';
import {customPropertiesStore, migrateBehavioralPropertyDefinition} from 'common/report/stores/custom-properties';
import {eventPropertiesStore} from 'common/report/stores/event-properties';
import {peoplePropertiesStore} from 'common/report/stores/people-properties';
import {currentProjectStore} from 'common/report/stores/project-details';
import {eventPropertiesDefinitionsStore} from 'common/report/stores/event-property-definitions';
import {Store} from 'common/report/stores/store';
import {getIconForPropertyType, getLabelForPropertyType, renamePropertyValue} from 'common/report/util/properties';
import {Cohort, MpProperty, PropertyDefinition} from 'common/util/definitions-types';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {
  getInlineCustomPropertyId,
  isBehavioralCustomProperty,
  isPersistedCustomProperty,
} from 'common/widgets/custom-property-modal/util';
import {DataGroup} from 'common/widgets/data-group-selector/types';
import {PropertyType, ResourceType} from 'common/report/constants';
import {promptSharing} from 'common/widgets/sharing';
import {ContextPanelData} from 'common/widgets/context-panel/types';
import {getTooltipDataForCohort, getTooltipDataForProperty} from 'common/widgets/context-panel/util';
import {SavedCohortItem} from 'common/widgets/query-entry-section/types';
import {SharableEntityType} from 'common/report/util/saved-entities/types';
import {isAllowedSuperadminOverride} from 'common/report/util/saved-entities';
import {
  eventPrefix as shareEventPrefix,
  ShareButtonUISource,
  track as shareTrack,
} from 'common/widgets/sharing/tracking';
import {
  fetchLookupTableProperties,
  getTempLookupTableMappings,
  lookupTablesStore,
  registerTempLookupTableMapping,
} from 'common/report/stores/lookup-tables';
import {
  ExpanderItemClickEvent,
  ItemClickEvent,
  SearchMatchResult,
  SubLabelTheme,
} from 'common/components/items-menu/types';
import {InlineCohortsModalCommitEvent} from 'common/widgets/inline-cohorts-modal/types';
import {propertyDefinitionsClient} from 'common/report/queries/app-api/property-definitions-client';
import {reporter} from 'common/report/error-reporting';
import {featuresStore} from 'common/report/stores/features';
import {assertUnreachable} from 'common/util/typescript';
import {MPProperties} from 'common/constants';
import {TagInputChangeEvent} from 'common/components/tag-input/types';
import {Visibility} from 'common/components/base/stackable';
import {chameleonReadyPromise, showTour, TourId} from 'common/util/chameleon';
import {ChangeEvent} from 'common/components/select/types';

import {ChildMenu, ChildMenuBaseAttrs, TooltipContextActionResult} from '../child-menu';
import {EventsController} from '../contexts/events-controller';
import {ActionType} from '../events-menu/types';
import {sortItemsByLabel} from '../util';
import {
  calculatePropertiesIntersection,
  CUSTOM_PROPERTY_RESOURCE_TYPE_MAPPING,
  filterToBookmarkablePropertyItem,
  getDataDefinitionsPropertyMenuItem,
  getItemUniqueKey,
  inferStagedItemType,
  propertyItemToObjectPropertyItem,
  sanitizeCustomProperty,
  sortUiUniquePropertyItems,
  storeCustomPropertyToProperty,
} from './util';
import {TrackingEvent} from './tracking';
import {
  AllPropertiesTab,
  AllTab,
  BookmarkablePropertyItem,
  CohortItem,
  EventsInPropertiesMenuTab,
  GetSortedMenuSections,
  IPropertiesMenu,
  MenuItemType,
  PropertiesMenuAttrs,
  PropertiesMenuJsxProps,
  PropertyCombineMode,
  PropertyItemAction,
  PropertyItemPersistedIdentifierKeys,
  SelectedPropertyItem,
  SelectedTab,
  SelectPropertyEvent,
  SortBy,
  SortOptions,
  StagedItemType,
  TypecastPillAction,
} from './types';
import {
  EventSection,
  EventSectionItem,
  PropertiesItemClickEvent,
  PropertiesItemFocusEvent,
  PropertySection,
  PropertySectionItem,
} from './item-menu-types';
import {
  ALL_USERS_COHORT,
  DATASET_PROPERTY,
  DATE_PROPERTY,
  DATETIME_UNIT_OPTION_SECTION_ITEMS,
  EXTRA_PROPERTY_DEFINITIONS_BY_TYPE,
  FIRST_EVENT_TIME_PROPERTY,
  OTHER_OPTIONS,
  SPECIAL_CONTROLS,
  SPECIAL_PROPERTIES,
  SpecialActions,
  TOP_PEOPLE,
} from './constants';
import {template} from './template';
import css from './index.styl?inline';

// number of items to show per section on the "All" tab
// remaining items are hidden under a `Show all` expander
const MENU_ITEM_LIMIT = 10;

const PropertyDefinitionStoreMap = {
  [ResourceType.User]: peoplePropertyDefinitionsStore,
  [ResourceType.Event]: eventPropertyDefinitionsStore,
};

enum EventPropertySource {
  Empty,
  Intersection,
  Store,
}

type DataGroupItem = DataGroup & {
  properties: any[];
  isLoading: boolean;
};

enum IncludeCustomPropertyType {
  All = `all`,
  None = `none`,
  PersistedOnly = `persisted-only`,
  PersistedNonBehavioralOnly = `persisted-non-behavioral-only`,
}

interface PropertiesMenuState {
  // used specifically to remove context panel from showing unexpectedly for custom props and cohorts
  modalShowing: boolean;
  dataGroups: {[id: string]: DataGroupItem};
  eventProperties: Array<PropertyDefinition | ProcessedArbProperty>;
  featureFlags: Set<string>;
  permissionList: any;
  lastMultiSelection: PropertySectionItem[];
  loadingCohorts: boolean;
  loadingEventProperties: boolean;
  loadingPeopleProperties: boolean;
  selectedTab: SelectedTab;
  stagedItemsStack: PropertySectionItem[];
  stagedItemType: StagedItemType | null;
  tooltipData: ContextPanelData;
  innerSearchFilter: string;
  getSortedMenuSections: GetSortedMenuSections;
  createMenuOpen: boolean;
  openedAt: number;
  sortValue: string;
}

export class PropertiesMenu extends ChildMenu<PropertiesMenuState, PropertiesMenuAttrs> implements IPropertiesMenu {
  private _eventPropertySource;
  private _context = null;
  private _cohortStoreCallback = null;
  private _customPropertiesStoreCallback = null;
  private _eventPropertiesStoreCallback = null;
  private _eventPropertiesStoreKey: string = null;
  private _peoplePropertiesStoreCallback = null;
  private _lookupTablesStoreCallback = null;
  private _searchInput: TagInput | InputComponent = null;

  public tooltipPositionParentElement: HTMLElement;

  static get attrsSchema(): AttrsSchema<ChildMenuBaseAttrs & PropertiesMenuAttrs> {
    return {
      ...super.attrsSchema,
      'cohort-multiselect': `boolean`,
      'cohort-operation-type': `string`,
      'apply-button': {
        type: `string`,
        default: null,
      },
      'apply-button-disabled': `boolean`,
      'allow-behavioral-property-creation': {
        type: `boolean`,
        description: `shows the 'Create behavioral property...' menu item in supported reports`,
      },
      'allow-typecast-pill-action': {
        type: `string`,
        description: `allows showing a pill labeled typecast next to eligible properties`,
        enum: Object.values(TypecastPillAction),
        default: null,
      },
      'enable-workspace-filter-restrictions': `boolean`,
      'disabled-items': {
        type: `json`,
        default: {}, // dict for fast inclusion lookups
        description: `sets disabled options for the underlying mp-items-menu`,
      },
      'include-custom-properties': {
        type: `string`,
        enum: Object.values(IncludeCustomPropertyType),
        default: IncludeCustomPropertyType.None,
        description: `whether to hide custom properties from properties menus`,
      },
      'exclude-dimension-properties': {
        type: `boolean`,
        description: `whether to hide dimension properties from properties menus`,
      },
      'exclude-lookup-table-properties': {
        type: `boolean`,
        description: `whether to hide user uploaded properties from a lookup table.
        Different from 'exclude-dimension-properties' since this still shows properties as mapped to a lookup table`,
      },
      'force-old-dropdowns': {
        type: `boolean`,
        description: `whether to force the old style property downs that the 'new_dropdowns' project feature flag is used to replace`,
      },
      'property-combine-mode': {
        type: `string`,
        default: PropertyCombineMode.Union,
        description: `defines how event properties should be merged`,
        enum: Object.values(PropertyCombineMode),
      },
      'show-all-tab': {
        type: `boolean`,
        description: `enables the "All" tab in the selection menu when there is more than one section`,
      },
      'show-all-users-cohort': {
        type: `boolean`,
        description: `enables the "All Users" cohort if cohort selection is enabled`,
      },
      'show-items-caret': {
        type: `boolean`,
        description: `enables the a caret icon in the selection items`,
      },
      'single-data-group': {
        type: `string`,
        description: `if set, shows group properties for a specific data group id`,
      },
      'other-options': {
        type: `json`,
        default: [],
        description: `list of items to show in the other section`,
      },
      'show-select-all': `boolean`,
      'select-all-disabled': `boolean`,
      'hide-recently-viewed': `boolean`,
      'hide-section-labels': `boolean`,
      'selectable-sensitive-properties': `boolean`,
      'description-edit-disabled': `boolean`,
      'menu-item-types': {
        type: `json`,
        description: `defines what type of items this menu will render (events, cohorts, user props, etc)`,
        default: [],
      },
    };
  }

  get config() {
    return {
      css,
      template,
      useShadowDom: true,

      contexts: [`eventsController` as const],
      defaultContexts: {eventsController: new EventsController()},

      defaultState: {
        modalShowing: false,
        dataGroups: {},
        eventProperties: [],
        featureFlags: null,
        permissionList: [],
        isLoading: false,
        loadingCohorts: false,
        loadingEventProperties: false,
        loadingPeopleProperties: false,
        selectedTab: AllTab,
        stagedItemType: null,
        lastMultiSelection: null,
        tooltipData: null,
        innerSearchFilter: ``,
        getSortedMenuSections: null,
        stagedItemsStack: [],
        createMenuOpen: false,
        openedAt: null,
        sortValue: `Suggested`,
      },

      helpers: {
        ...super.config.helpers,
        StagedItemTypes: StagedItemType,
        getMenuSections: () => this.getMenuSections(),
        getToggleTabs: () => this.getToggleTabs(),
        handleClickItem: ({detail: {item, section, ...detail}}: PropertiesItemClickEvent) => {
          // Special Action - An item presents a Create Modal
          const isSpecialAction = Object.values(SpecialActions).includes(item.value);
          if (isSpecialAction) {
            // Todo: Type this as a new section
            this.showCreateItemModal(item.value);
            return;
          }

          const menuItemType = section?.type;

          // Stageable Item - An item keeps the user in the menu showing a new screen
          if (section.type !== MenuItemType.Events) {
            const propertyItem = item as PropertySectionItem;
            const stagedItemType = inferStagedItemType(propertyItem);
            if (stagedItemType && this.canStageItemType(stagedItemType)) {
              this.updateStagedItem(item as PropertySectionItem, stagedItemType);
              return;
            }
          }

          // Selectable Item - An item is committed to the Query
          let itemValue = item;
          if (this.hasStagedItem()) {
            itemValue = this.applyContextFromStagedItems(itemValue);
          }
          itemValue = menuItemType === MenuItemType.Events ? itemValue.value : itemValue;
          this.selectItem(itemValue, {
            ...detail,
            onItemClicked: true,
            menuItemType,
          });
        },
        getSortingOptionSectionItems: () => {
          return Object.entries(SortOptions).map(([label, value]) => ({
            label,
            value,
            isSelected: label === this.state.sortValue,
          }));
        },
        handleSortChange: ({detail}: ChangeEvent) => {
          this.update({sortValue: detail.value});
        },
        onItemPillClick: (ev) => this.handleItemPillClick(ev.detail.item),
        onAllSelected: (ev) => this.dispatchEvent(new CustomEvent(`selectAll`, {detail: ev.detail})),
        getCreateItemSections: () => {
          return [
            {
              items: [
                {label: `Create event property`, value: SPECIAL_CONTROLS.CREATE_CUSTOM_EVENT_FORMULA.value},
                {label: `Create user property`, value: SPECIAL_CONTROLS.CREATE_CUSTOM_PEOPLE_FORMULA.value},
                {label: `Create cohort`, value: SPECIAL_CONTROLS.CREATE_INLINE_COHORT.value},
                ...(this.isBehavioralPropertiesEnabled()
                  ? [
                      {
                        label: `Create behavioral property`,
                        value: SPECIAL_CONTROLS.CREATE_CUSTOM_BEHAVIORAL_PROPERTY.value,
                      },
                    ]
                  : []),
              ],
            },
          ];
        },
        onCreateItemClick: ({detail}: ItemClickEvent<SpecialActions>) => this.showCreateItemModal(detail.item.value),
        unstageProperty: () => this.updateStagedItem(null, null),
        handleFocusItem: debounce(
          ({detail: {item, section}}: PropertiesItemFocusEvent) => this.showContextPanel(item, section),
          100,
        ),
        useMultipleSelection: () =>
          this.attr(`multiple`) || (this.attr(`cohort-multiselect`) && this.isCohortSelection()),

        getDefaultFormulaName: () => {
          const entityNames = this.getCustomEntities([ResourceType.Event, ResourceType.User]).map((item) => item.value);
          const prefix = this.attr(`cohort-operation-type`)
            ? `Custom ${this.attr(`cohort-operation-type`)}`
            : `Custom formula`;
          for (let i = 1; ; i++) {
            const name = `${prefix} ${i}`;
            if (!entityNames.includes(name)) {
              return name;
            }
          }
        },

        shouldShowCreateItems: () => {
          return !(
            this.attr(`hide-edit-controls`) ||
            [
              StagedItemType.Datetime,
              StagedItemType.Dimension,
              StagedItemType.Object,
              StagedItemType.Typecast,
            ].includes(this.getStagedItemType())
          );
        },

        handleCreateButtonClicked: () => {
          if (this.state.createMenuOpen) {
            this.update({createMenuOpen: false});
          } else {
            // hide context panel if it's open by setting tooltipData to null
            this.update({createMenuOpen: true, tooltipData: null});
          }
        },

        handleCreateMenuChange: (ev: CustomEvent) => {
          if (ev.detail.state === Visibility.Closed) {
            this.update({createMenuOpen: false});
          }
        },

        handleTooltipContextEditDescription: (ev) => this.helpers.propagateEvent(ev),

        handleTooltipContextAction: (ev) => {
          const {name} = ev.detail;
          this.update({modalShowing: true});
          if ([`shareCohort`, `shareCustomProperty`].includes(name)) {
            this.handleShareTooltipContextAction(ev);
          } else {
            const result = this.handleTooltipContextAction(ev);
            if (!result || !result.handled) {
              this.helpers.propagateEvent(ev);
            }
          }
          this.helpers.closeContextPanel();
        },

        searchInputInserted: (vnode) => {
          requestAnimationFrame(() => vnode.elm.focus());
          this._searchInput = vnode.elm;
        },

        handleTagInputChange: (ev: TagInputChangeEvent) => {
          switch (ev.detail.action) {
            case `removeTag`:
              this.helpers.unstageProperty();
              break;
            case `text`:
              this.update({innerSearchFilter: ev.detail.value});
              break;
            default:
              assertUnreachable(ev.detail);
          }
        },

        handleInnerSearchInput: (ev) => {
          ev.stopPropagation();
          this.update({innerSearchFilter: ev.target.value});
        },

        selectOption: (ev) => {
          const {selected} = ev.detail;

          this.update({selectedTab: selected});
          this.trackEvent(TrackingEvent.SelectPropertiesDropdownOption, {
            context: this._context,
            selectedTab: selected,
          });
          this.dispatchEvent(new CustomEvent(`selectoption`, {detail: {selected}}));
        },
        getStagedItemLabel: (separator = `.`) =>
          this.state.stagedItemsStack.map(({name}) => name || ``).join(separator),

        getHeaderLabel: () => {
          const label = this.helpers.getStagedItemLabel();

          switch (this.getStagedItemType()) {
            case StagedItemType.Typecast:
              return `${label} Typecast`;
            case StagedItemType.Datetime:
              return `Date Time Unit`;
            case StagedItemType.Dimension:
              return label;
            case StagedItemType.Object:
              return label;
            case StagedItemType.User:
            case StagedItemType.Group:
            case StagedItemType.Cohort:
              return this.helpers.getStagedItemLabel(` > `);
            default:
              return null;
          }
        },

        getSearchFilter: () => this.getSearchFilter(),

        getObjectMenuSection: () => {
          const [baseProperty, ...nestedProps] = this.state.stagedItemsStack.filter(
            (item) => !isNil(item.type), // filter out non-property items
          );

          const objectProperty = propertyItemToObjectPropertyItem({
            ...baseProperty,
            ...(nestedProps.length
              ? {
                  propertyObjectKey: nestedProps.map((property) => property.value),
                  joinPropertyType: PropertyType.Object,
                }
              : {}),
          });

          const client =
            objectProperty.resourceType === ResourceType.Event ? eventPropertiesClient : peoplePropertiesClient;
          const params = {
            objectProperty,
            projectId: this.sourceAttrs.projectId,
            workspaceId: this.sourceAttrs.workspaceId,
          };

          const properties = client.getCachedResponse(params);

          if (!properties) {
            // Asynchronously fetches or looks up nested object property in cache
            client.query(params, {forceCache: true}).then(() => this.update());

            return [
              {
                isLoading: true,
                items: [],
              },
            ];
          }

          const forExperiments = baseProperty.value === MPProperties.Experiments;

          return [
            {
              items: this.getPropertyMenuItems({
                properties: properties.map((property) => {
                  return {
                    ...property,
                    label: forExperiments
                      ? renamePropertyValue(property.name, MappedPropertyValues.ExperimentId)
                      : property.label,
                    type: forExperiments ? PropertyType.String : property.type,
                    isSelected: false,
                  };
                }),
                resourceType: baseProperty.resourceType,
              }),
            },
          ];
        },

        getGroupMenuSection: (item: PropertySectionItem) => {
          const dataGroupID = item.value.groupId;
          return [
            {
              items: this.getPropertyMenuItems({
                properties: this.state.dataGroups[dataGroupID].properties,
                resourceType: ResourceType.User,
              }),
            },
          ];
        },

        getCohortMenuSection: () => {
          return this.getCohortsMenuSections();
        },

        getDimensionMenuSection: (item: PropertySectionItem) => {
          const dataGroupId = item.tempDataGroupId || item.dimensionDataGroupId;
          const dimension = item.value;
          const lookupTableProperties = (lookupTablesStore.value[dataGroupId]?.properties || []).map((property) => {
            return {
              ...property,
              dimensionDataGroupId: dataGroupId,
              value: [dimension, property.name],
            };
          });

          if (lookupTableProperties.length === 0) {
            reporter.error(`[lookup] properties-menu: blank lookup table shown`, {
              projectId: this.attr(`project-id`),
              workspaceId: this.attr(`workspace-id`),
              userId: this.attr(`user-id`),
              dataGroupId,
            });
          }

          const properties = [
            // Include the dim prop underlying property.
            {
              name: item.name,
              label: item.label,
              type: item.joinPropertyType,
              value: dimension,
              customProperty: item.customProperty,
              customPropertyId: item.customPropertyId,
            },
            ...lookupTableProperties,
          ];

          return [
            {
              items: this.getPropertyMenuItems({
                properties,
                resourceType: item.resourceType,
              }),
            },
          ];
        },

        getDatetimeMenuSections: () => {
          return [{items: DATETIME_UNIT_OPTION_SECTION_ITEMS}];
        },

        getTypecastMenuSections: ({type}) => {
          const typeCastTypes = Object.values(TYPECAST_PROPERTY_TYPES);
          const types = [...typeCastTypes, ...(typeCastTypes.includes(type) ? [] : [type])];

          const items = types.map((typeCastType) => {
            return {
              icon: getIconForPropertyType(typeCastType),
              isSelected: typeCastType === type,
              label: getLabelForPropertyType(typeCastType),
              value: typeCastType,
            };
          });

          return [{items}];
        },

        sectionsFromInput: (input: string) => {
          const selectedTab = this.state.selectedTab;
          const items = [];

          switch (selectedTab) {
            case EventsInPropertiesMenuTab:
              items.push({
                label: input,
                value: {value: input, label: this.isNewMenu() ? `"${input}"` : input},
                icon: !this.isNewMenu() && `plus`,
              });
              break;
            case ResourceType.Event:
              items.push({
                type: PropertyType.String,
                propertyDefaultType: PropertyType.String,
                label: this.isNewMenu() ? `"${input}"` : input,
                value: input,
                isSelected: this.isItemSelected(input, this.attr(`selected`)),
                resourceType: selectedTab,
                icon: !this.isNewMenu() && `plus`,
              });
              break;
            case ResourceType.User:
              items.push({
                type: peoplePropertyDefinitionsStore?.value[input]?.type || PropertyType.String,
                propertyDefaultType: peoplePropertyDefinitionsStore?.value[input]?.type || PropertyType.String,
                label: this.isNewMenu() ? `"${input}"` : input,
                value: input,
                isSelected: this.isItemSelected(input, this.attr(`selected`)),
                resourceType: selectedTab,
                icon: !this.isNewMenu() && `plus`,
              });
              break;
            default:
              break;
          }

          return [{items: items.filter(Boolean), type: selectedTab}];
        },

        //#region Custom properties
        closeContextPanel: () => {
          this.update({tooltipData: null});
        },
        closeModal: () => {
          this.update({modalShowing: false});
          this.helpers.closeContextPanel();
        },
        deletedCustomProperty: () => {
          const selectedCustomPropertyId = (this.attr(`selected`) || {}).customPropertyId;
          // when filter menu is open, `selected` on the underlying
          // properties-menu is set to null, so we have to manually delete
          // ourselves here. the delete callback can handle anything else.
          if (!selectedCustomPropertyId) {
            this.dispatchEvent(new CustomEvent(`delete`));
          }
          this.helpers.closeModal();
        },
        applyCustomProperty: (customProp, originalCustomPropId) => {
          // originalCustomPropId will be the same as customProp.customPropertyId
          // unless we are saving a temporary custom property for the first time
          // (in which case it will be the old temporary id) or we are creating
          // a custom property for the first time (in which case it will be null)
          let {resourceType, customProperty, customPropertyId} = customProp;
          // we use 'user' and 'event' in the new query builder
          if (Object.keys(INSIGHTS_PROPERTY_FILTER_RESOURCE_TYPE_MAPPING).includes(resourceType)) {
            resourceType = INSIGHTS_PROPERTY_FILTER_RESOURCE_TYPE_MAPPING[resourceType];
          }
          customProperty = sanitizeCustomProperty(customProperty);
          const property = {
            ...customProp,
            label: customProperty.name,
            customProperty,
            customPropertyId: isPersistedCustomProperty(customPropertyId) ? Number(customPropertyId) : customPropertyId,
            resourceType,
          };

          let selected = this.attr(`selected`);
          if (!selected) {
            selected = [];
          } else if (!Array.isArray(selected)) {
            selected = [selected];
          }
          const index = selected.findIndex(
            (selectedItem) =>
              selectedItem.customPropertyId === property.customPropertyId ||
              selectedItem.customPropertyId === originalCustomPropId,
          );

          const selectedCustomProp = selected[index];
          const {joinPropertyType} = customProperty;
          if (joinPropertyType) {
            property.joinPropertyType = joinPropertyType;
            if (Array.isArray(selectedCustomProp.value)) {
              // dimension property selected
              property.dataGroupId = customProperty.mappedDataGroupId;
              property.value = [property.value, selectedCustomProp.value[1]];
              property.propertyDefaultType = selectedCustomProp.propertyDefaultType;

              property.type = selectedCustomProp.type;
              property.propertyType = selectedCustomProp.type;
            } else {
              property.type = joinPropertyType;
              property.propertyType = joinPropertyType;
            }
          }

          if (index > -1) {
            this.replaceItem(property, index, {commit: true});
          } else {
            this.selectItem(property, {commit: true});
          }
          this.helpers.closeModal();
        },
        onApplyButtonClick: () => {
          if (!this.attr(`apply-button-disabled`)) {
            this.dispatchSelectEvent({
              item: this.getArraySelection(),
              rawItem: null,
              autoTriggered: false,
              onItemClicked: false,
              commit: true,
            });
          }
        },
        hasSelectSortingHeader: (): boolean => {
          return this.isNewMenu() && [AllPropertiesTab, EventsInPropertiesMenuTab].includes(this.state.selectedTab);
        },
        //#endregion
      },
    };
  }

  shouldCloseOnSelect() {
    return !this.attr(`apply-button`);
  }

  customPropertiesStoreCallback() {
    const selected = this.attr(`selected`) as PropertySectionItem;
    const selectedCustomPropertyId = (selected || {}).customPropertyId;
    if (selectedCustomPropertyId in customPropertiesStore.value) {
      const newCustomPropertyId = customPropertiesStore.value[selectedCustomPropertyId].newCustomPropertyId;

      const property = storeCustomPropertyToProperty(newCustomPropertyId || selectedCustomPropertyId);

      const selectedCustomProperty = sanitizeCustomProperty((selected || {}).customProperty);
      if (newCustomPropertyId || !isEqual(property.customProperty, selectedCustomProperty)) {
        // update for breakdown menu and filter menus that are closed (the opened one has no `selected` set)
        const item = this.getPropertyMenuItem({
          property,
          resourceType: property.resourceType,
        });
        this.selectItem(item, {autoTriggered: true});
      }
    } else if (
      isPersistedCustomProperty(selectedCustomPropertyId) &&
      Object.keys(customPropertiesStore.value).length > 0
    ) {
      // delete
      this.dispatchEvent(new CustomEvent(`delete`));
    }
  }

  generateShowGroupPropertiesItemForGroup = (groupID: string): PropertySectionItem => {
    const dataGroup = this.state.dataGroups[groupID];
    const displayName = dataGroup ? dataGroup.displayName : `Group ${groupID}`;
    return {
      label: displayName,
      name: displayName,
      stagedItemType: StagedItemType.Group,
      value: {
        value: `group`,
        groupId: groupID,
      },
      hasCaret: true,
      persistCaret: true,
      propertyDefaultType: null,
      resourceType: null,
      type: null,
    };
  };

  generateShowObjectPropertiesItemForKey = (key: string, resourceType: ResourceType): PropertySectionItem => {
    return {
      type: PropertyType.Object,
      propertyDefaultType: PropertyType.Object,
      label: key,
      name: key,
      resourceType,
      value: key,
      stagedItemType: StagedItemType.Object,
    };
  };

  public connectedCallback() {
    super.connectedCallback();
    this.fetchData();
    this.updateFromSelection();

    this._customPropertiesStoreCallback = () => this.customPropertiesStoreCallback();

    customPropertiesStore.subscribe(this._customPropertiesStoreCallback);
    customPropertiesStore.fetch({
      projectId: this.attr(`project-id`),
      workspaceId: this.attr(`workspace-id`),
    });

    this.loadStoreValues();

    if (this.attr(`open`)) {
      this.update({
        openedAt: new Date().getTime(),
      });
    }
  }

  public disconnectedCallback() {
    customPropertiesStore.unsubscribe(this._customPropertiesStoreCallback);

    this.getEventPropertiesStore().unsubscribe(this._eventPropertiesStoreCallback, this._eventPropertiesStoreKey);

    peoplePropertiesStore.unsubscribe(this._peoplePropertiesStoreCallback);
    lookupTablesStore.unsubscribe(this._lookupTablesStoreCallback);

    if (this.attr(`project-has-cohorts`)) {
      const dataGroupId = this.attr(`data-group-id`);
      if (dataGroupId && this.attr(`support-b2b-cohorts`)) {
        cohortsStore.unsubscribe(this._cohortStoreCallback, dataGroupId);
      } else {
        cohortsStore.unsubscribe(this._cohortStoreCallback);
      }
    }
    super.disconnectedCallback();
  }

  handleCloseMenu() {
    this.helpers.handleFocusItem.cancel();
  }

  public loadStoreValues() {
    // all properties-menus upload any inline custom properties they host.
    const inlineCustomProperties = flatten(
      this.getPropertyResourceTypes().map((type) => {
        return this.getSelectedCustomFormula(type);
      }),
    )
      .map((cur) => {
        if (cur.customProperty) {
          cur.customProperty = migrateBehavioralPropertyDefinition(cur.customProperty);
        }
        return cur;
      })
      .reduce((acc, cur) => {
        const {customPropertyId, customProperty} = cur;
        return {
          ...acc,
          [getInlineCustomPropertyId(customPropertyId)]: customProperty,
        };
      }, {});

    customPropertiesStore.update(inlineCustomProperties);

    // load all inline cohorts into cohort store
    cohortsStore.update(
      this.getInlineCohortItems().reduce((acc, cur: PropertySectionItem) => {
        return {
          ...acc,
          [cur.value.cohort.raw_cohort.unsavedId]: {
            name: cur.label,
            created_by: cur.createdBy,
            raw_cohort: cur.value.cohort.raw_cohort,
          } as SerializedInlineCohort,
        };
      }, {}),
    );
  }

  public resetStage() {
    this.update({
      stagedItemsStack: [],
      stagedItemType: null,
    });
  }

  public hasStagedItem(): boolean {
    return this.state.stagedItemsStack?.length > 0;
  }

  public peekStagedItem() {
    return this.state.stagedItemsStack[this.state.stagedItemsStack.length - 1];
  }

  /* A true value signifies that query-block parent renders mp-input and updates the searchFilter attribute */
  public isContentSearchable() {
    return !this.isNewMenu() && !this.hasStagedItem();
  }

  /* A true value signifies that the properties-menu will render mp-input and updates the searchFilter attribute */
  public isStagedItemSearchable(): boolean {
    return this.hasStagedItem() && [StagedItemType.Dimension, StagedItemType.Object].includes(this.getStagedItemType());
  }

  /**
   * Sets the active tab to the section that the currently selected values belongs to.
   * If properties from multiple tabs are selected, sets the "All" tab when enabled, or to the first tab otherwise
   */
  public updateFromSelection() {
    const currentTab = this.state.selectedTab;
    let selected = this.attr(`selected`) || [];
    selected = Array.isArray(selected) ? selected : [selected];

    const resourceType = selected[0]?.resourceType;
    const dataGroupId = selected[0]?.dataGroupId;
    const isCohort =
      Array.isArray(this.attr(`selected`)) &&
      (!resourceType || resourceType === ResourceType.Cohort) &&
      (!this.attr(`menu-item-types`) ||
        this.attr(`menu-item-types`).length === 0 ||
        this.attr(`menu-item-types`)?.includes[MenuItemType.Cohorts]);
    const isDimProp = this.isDimensionSelected(selected[0]);
    const isB2BTab = dataGroupId && !isDimProp;

    let selectedTab;
    if (isCohort) {
      selectedTab = ResourceType.Cohort;
    } else if (this.isNewMenu()) {
      selectedTab = AllPropertiesTab;
    } else if (isB2BTab) {
      selectedTab = dataGroupId;
    } else {
      selectedTab = resourceType;
    }
    const tabs = this.getToggleTabs();
    if (selectedTab) {
      if (resourceType === ResourceType.Other) {
        this.update({selectedTab: this.getDefaultToggleTab()});
        return;
      }
      this.update({selectedTab});
    } else if (tabs.length && !tabs.find((tab) => tab.value === this.getDefaultToggleTab())) {
      this.update({selectedTab: tabs[0].value});
    } else {
      this.update({selectedTab: this.getDefaultToggleTab()});
    }

    if (currentTab !== this.state.selectedTab) {
      this.dispatchEvent(new CustomEvent(`tabChanged`));
    }
  }

  maybeNavigateToSelected() {
    const selected = this.attr(`selected`) as PropertySectionItem | PropertySectionItem[];
    const selectedItem = Array.isArray(selected) ? selected[0] : selected;
    if (!selectedItem) {
      return;
    }

    if (this.canShowLookupTablePropertyMenu({selected})) {
      // open dimension property submenu
      const property = this.getLookupTablePropertyFromSelected(selectedItem);
      if (property) {
        const item = this.getPropertyMenuItem({
          property,
          resourceType: selectedItem.resourceType,
        });
        this.updateStagedItem(item, StagedItemType.Dimension);
      }
    }

    // Unfurl/restore nested object properties to stagedItemsStack
    if (selectedItem?.joinPropertyType === PropertyType.Object && selectedItem?.propertyObjectKey?.length) {
      [selectedItem.value, ...selectedItem.propertyObjectKey.slice(0, -1)].forEach((propertyKey) => {
        this.updateStagedItem(
          this.generateShowObjectPropertiesItemForKey(propertyKey, selectedItem.resourceType),
          StagedItemType.Object,
        );
      });
    }
  }

  public attributeChangedCallback(attr, oldVal, newVal) {
    super.attributeChangedCallback(attr, oldVal, newVal);

    // only update properties if project-id has already been defined
    if (!this.initialized || !this.attr(`project-id`)) {
      return;
    }

    switch (attr) {
      case `all-data-groups`:
      case `data-group-id`:
      case `event-names`:
      case `include-custom-properties`:
      case `project-has-cohorts`:
      case `hide-events`:
      case `project-has-people`:
      case `project-id`:
      case `property-combine-mode`:
      case `single-data-group`:
        this.fetchData();
        this.updateFromSelection();
        break;
      case `selected`:
        this.loadStoreValues();
        this.updateFromSelection();
        break;
      case `open`:
        if (this.attr(`open`)) {
          this.trackEvent(TrackingEvent.OpenPropertiesDropDown);
          // stages lastMultiSelection from initial selected state
          const selectedArray = this.getArraySelection();
          this.update({
            lastMultiSelection: Array.from(selectedArray),
            openedAt: new Date().getTime(),
            sortValue: this.config.defaultState.sortValue,
          });
          this.maybeNavigateToSelected();
        } else {
          this.update({
            tooltipData: null,
            innerSearchFilter: ``,
          });
          this.resetStage();
        }
        break;
      case `search-filter`:
        this.update({searchFilter: newVal});
        break;
      default:
        break;
    }

    // TODO don't call this on every attribute update (e.g. not on `search-filter` when user types a new character)
    this.customPropertiesStoreCallback();
  }

  public getSelectedTab() {
    return this.state.selectedTab;
  }

  public isDatetimeSelection() {
    return this.getStagedItemType() === StagedItemType.Datetime;
  }

  public isTypecastSelection() {
    return this.getStagedItemType() === StagedItemType.Typecast;
  }

  public isCohortSelection() {
    return this.state.selectedTab === ResourceType.Cohort;
  }

  public getStagedItemType(): StagedItemType {
    const {stagedItemType} = this.state;
    if (!this.hasStagedItem() && stagedItemType) {
      // Ensure there a stack before returning type as stagedItem and stagedItemType can get out of sync.
      // Out use of stagedItemType will be refactored out; Until then, warn on any mismatch.
      reporter.error(`StagedItemType<${stagedItemType}> exists when there is not StagedItemStack`);
      return null;
    }
    return stagedItemType;
  }

  // overriding ChildMenu::getSelectedPropertyName() is intentional, properties-menu uses Store objects to prefer canonical entity names
  public getSelectedPropertyName(): string {
    const selected = this.getSelectedProperty();
    const firstItem = Array.isArray(selected) ? selected[0] : selected;

    if (!firstItem) {
      return ``;
    }

    // use canonical cohort names when available
    if (firstItem.resourceType === ResourceType.Cohort && Number.isInteger(firstItem.value)) {
      const label = cohortsStore.value[firstItem.value]?.name || firstItem.label;
      return firstItem.negated ? `Not in ${label}` : label;
    }

    return super.getSelectedPropertyName();
  }

  public selectedLabel() {
    const selected = this.getSelectedProperty();
    let label;
    if (selected && selected.renamed) {
      label = `${selected.renamed} (${selected.label})`;
    } else {
      label = this.getSelectedPropertyName() || `Select a property`;
    }

    return label;
  }

  public selectedIcon() {
    let selected = this.attr(`selected`) || [];
    selected = Array.isArray(selected) ? selected[0] : selected;
    const resourceType = selected?.resourceType;

    switch (resourceType) {
      case ResourceType.User: {
        return selected.dataGroupId ? `group-attribute` : `profile-attribute`;
      }
      case ResourceType.Event:
        return `event-attribute`;
      case ResourceType.Cohort:
        return `community`;
      default: {
        // special prop icons
        if (selected?.value === TOP_PEOPLE.value) {
          return TOP_PEOPLE.icon;
        }
        return null;
      }
    }
  }

  public getPersistenceKey() {
    return `properties-menu`;
  }

  private getDefaultToggleTab() {
    return this.isNewMenu() ? AllPropertiesTab : AllTab;
  }

  private async clickEditCustomProperty(property) {
    const {appliedValue, deletedId} = await this.promptCustomPropertiesModal(property);
    if (appliedValue) {
      this.helpers.applyCustomProperty(appliedValue, property.customPropertyId);
      this.trackEvent(TrackingEvent.EditCustomProperty, {
        context: this._context,
        name: property.name,
        resourceType: property.resourceType,
      });
    }
    if (deletedId) {
      let selected = this.attr(`selected`);
      if (!selected) {
        selected = [];
      } else if (!Array.isArray(selected)) {
        selected = [selected];
      }
      const index = selected.findIndex((selectedItem) => selectedItem.customPropertyId?.toString() === deletedId);
      this.removeItem(index, {commit: true});
    }
  }

  private getEventPropertiesStore() {
    let evPropsStore = null;
    if (this.showDataDefinitionsDropdowns()) {
      evPropsStore = eventPropertiesDefinitionsStore;
    } else {
      evPropsStore = eventPropertiesStore;
    }

    return evPropsStore;
  }

  private async handleItemPillClick(item) {
    if (item.pillAction === PropertyItemAction.Typecast) {
      const action = this.attr(`allow-typecast-pill-action`);
      switch (action) {
        case TypecastPillAction.Typecast:
          this.updateStagedItem(item, StagedItemType.Typecast);
          break;
        case TypecastPillAction.TriggerTutorial:
          this.selectItem(item, {commit: true});
          await chameleonReadyPromise();
          showTour(TourId.TypecastInOverflow);
          break;
        default:
          assertUnreachable(action);
      }
    } else {
      console.warn(`Invalid primary action for property item`);
    }
  }

  private updateStagedItem(stagedItem: PropertySectionItem, stagedItemType) {
    const {stagedItemsStack: stagedItemsStack, stagedItemType: currentStagedItemType} = this.state;
    let updatedStack = [];
    let updatedStagedItemType = stagedItemType;
    if (stagedItem === null) {
      // Immutable pop
      updatedStack = [...stagedItemsStack].slice(0, -1);
      const itemToBeStaged: PropertySectionItem = updatedStack[updatedStack.length - 1];
      // TODO: This logic isn't very clear. The idea is if we're unstaging the leaf prop,
      // we need to update the current stagedItemType. In Shawn's logic, we just use whatever
      // the existing menuType is but generically we need to use the actual menuType of the new staged menu

      if (updatedStack.length > 0) {
        updatedStagedItemType = isNil(itemToBeStaged?.stagedItemType)
          ? currentStagedItemType
          : itemToBeStaged.stagedItemType;
      } else {
        updatedStagedItemType = null;
      }
    } else if (stagedItem?.path?.length) {
      updatedStack = [...stagedItem.path, stagedItem];
    } else {
      updatedStack = [...stagedItemsStack, stagedItem];
    }

    this.update({
      stagedItemsStack: updatedStack,
      stagedItemType: updatedStagedItemType,
      innerSearchFilter: ``,
      searchFilter: ``,
    });

    this.dispatchEvent(
      new CustomEvent(`stagedproperty`, {
        detail: {
          item: updatedStack[updatedStack.length - 1] || null,
          stack: updatedStack,
        },
      }),
    );

    requestAnimationFrame(() => {
      // focus and scroll to top item
      this.itemsMenuEl?.focusItem(0);

      // set browser focus to search input
      this._searchInput?.focus();
    });
  }

  private showCreateItemModal(value: SpecialActions) {
    switch (value) {
      case SpecialActions.CreateCustomEventFormula:
        return this.createCustomProperty(ResourceType.Event, false);
      case SpecialActions.CreateCustomPeopleFormula:
        return this.createCustomProperty(ResourceType.User, false);
      case SpecialActions.CreateCustomBehavioralProperties:
        return this.createCustomProperty(ResourceType.Event, true);
      case SpecialActions.CreateInlineCohort:
        return this.createInlineCohort();
      default:
        assertUnreachable(value);
    }
  }

  handleExpandItemClick({detail: {section, sectionIdx}}: ExpanderItemClickEvent) {
    if (section.label?.includes(`Hidden`)) {
      this.trackEvent(TrackingEvent.ShowHiddenEntity);
    } else {
      if (this.isNewMenu()) {
        this.trackEvent(TrackingEvent.ExpandedSection, {
          context: this._context,
          'Number of items in section': section.items.length,
          'Search - filter': this.state.innerSearchFilter,
          'Section index': sectionIdx,
          'Selected tab': this.state.selectedTab,
        });
      }
    }
  }

  private handleCohortUpdate(cohortStagedValue, cohortSavedValue) {
    if (cohortStagedValue || cohortSavedValue) {
      this.commitCohort(cohortStagedValue ?? cohortSavedValue);
    }
    this.helpers.closeModal();
  }

  private promptCustomPropertiesModal(property, forBehavioralProperty = false) {
    const attrs = {
      'default-name': this.helpers.getDefaultFormulaName(),
      'behavioral-properties-mode': forBehavioralProperty,
      'event-names': this.attr(`event-names`),
      'enable-workspace-filter-restrictions': this.attr(`enable-workspace-filter-restrictions`),
      open: true,
      'project-id': this.attr(`project-id`),
      'user-id': this.attr(`user-id`),
      property: JSON.stringify(property),
      'track-name': this.attr(`track-name`),
      'workspace-id': this.attr(`workspace-id`),
    };
    this.update({modalShowing: true});
    return promptCustomProperties(attrs);
  }

  private promptInlineCohortsModal(cohort: CohortModel) {
    const attrs = {
      'can-save': this.attr(`project-has-cohorts`),
      'can-edit': isAllowedSuperadminOverride(cohort) || cohort.can_update_basic,
      'data-group-id': this.attr(`data-group-id`),
      elref: `inlineCohortsModal`,
      'is-new-cohort-definition': !cohort?.id,
      'can-apply-unsaved-cohort': !cohort?.id,
      name: cohort?.name,
      open: true,
      'project-has-cohorts': this.attr(`project-has-cohorts`),
      'support-b2b-cohorts': this.attr(`support-b2b-cohorts`),
      'project-id': this.attr(`project-id`),
      cohort: cohort?.serialize(),
      'track-name': this.attr(`track-name`),
      type: this.attr(`cohort-operation-type`),
      'user-id': this.attr(`user-id`),
      'workspace-id': this.attr(`workspace-id`),
      'show-cohort-count': true,
    };
    this.update({modalShowing: true});
    return promptInlineCohorts(attrs);
  }

  private addSelectedItemToRecent(item) {
    if (this.isInlineCohort(item) || (item.customProperty && !isPersistedCustomProperty(item.customPropertyId))) {
      return;
    }

    this.addRecentItem(item.resourceType, pick(item, PropertyItemPersistedIdentifierKeys));
  }

  private selectItem(
    item: EventSectionItem | PropertySectionItem,
    {
      // Was an item in the properties menu clicked?
      onItemClicked = false,
      // Indicates whether the item selection is user triggered or automatically triggered (e.g.
      // custom properties / cohorts store callback). Note that this is not just the inverse of
      // onItemClicked. For example, if a user is creating a custom property via. the modal
      // autoTriggered will be false, and onItemClicked will also be false (since an item wasn't
      // clicked in the menu)
      autoTriggered = false,
      commit = false,
      searchFilter,
      labelMatch,
      subLabelMatch,
      customMatch,
      menuItemType,
    }: {
      onItemClicked?: boolean;
      autoTriggered?: boolean;
      commit?: boolean;
      searchFilter?: string;
      labelMatch?: SearchMatchResult;
      subLabelMatch?: SearchMatchResult;
      customMatch?: SearchMatchResult;
      menuItemType?: MenuItemType;
    } = {},
  ) {
    if (this.canShowLookupTablePropertyMenu({type: (item as PropertySectionItem).type}) && autoTriggered) {
      return;
    }

    const selectedTab = this.state.selectedTab;

    const bookmarkableSelection = filterToBookmarkablePropertyItem(
      pickBy(item, (value) => value !== undefined) as PropertySectionItem,
    );
    if (this.helpers.useMultipleSelection()) {
      // if item was not removed, add it to the selection
      const baseSelection = this.getArraySelection() as BookmarkablePropertyItem[];
      const selectionArray = baseSelection.filter((selectedItem) =>
        selectedItem.value &&
        isSerializedInlineCohort(selectedItem.value.cohort) &&
        isSerializedInlineCohort(item.value.cohort)
          ? !isEqual(selectedItem.value.cohort.raw_cohort, item.value.cohort.raw_cohort)
          : selectedItem.value !== item.value,
      );
      if (baseSelection.length === selectionArray.length) {
        selectionArray.push(bookmarkableSelection);
      }
      this.dispatchSelectEvent({
        item: selectionArray,
        rawItem: item,
        onItemClicked,
        autoTriggered,
        commit,
        menuItemType,
      });
    } else {
      this.dispatchSelectEvent({
        item: bookmarkableSelection,
        rawItem: item,
        onItemClicked,
        autoTriggered,
        commit,
        menuItemType,
      });
    }

    this.addSelectedItemToRecent(bookmarkableSelection);

    if (onItemClicked) {
      const propertySectionItem = item as PropertySectionItem;
      this.trackSelection(
        {
          eventName: TrackingEvent.SelectProperty,
          props: {
            'item type': menuItemType,
            'Selected label': item.label,
            'Selected tab': selectedTab,
            'Selected value': item.value,
            'Selected resource type': propertySectionItem.resourceType,
            'is custom property': !!propertySectionItem.customProperty,
            'is hidden entity': !!propertySectionItem.hidden,
            'is recently viewed': !!propertySectionItem.isRecentlyViewed,
            propertyDefaultType: propertySectionItem.propertyDefaultType,
            propertyType: propertySectionItem.propertyType,
            'Search - filter': searchFilter,
            'Search - label match': labelMatch,
            'Search - sublabel match': subLabelMatch,
            'Search - custom match': customMatch,
            'Staged items': this.state.stagedItemsStack.map((item) => item.label),
            'Elapsed time (ms) since menu opened': new Date().getTime() - this.state.openedAt,
          },
        },
        false, // Making this not cancelable so it's always tracked. https://mixpanel.slack.com/archives/C0KN5EVB8/p1604641156158700
      );
    }
  }

  private replaceItem(
    item,
    index,
    {
      onItemClicked = false,
      commit = false,
      autoTriggered = false,
    }: {onItemClicked?: boolean; commit?: boolean; autoTriggered?: boolean} = {},
  ) {
    // Pickout undefined values to prevent duplicate actions triggering
    const selection = filterToBookmarkablePropertyItem(
      pickBy(item, (value) => value !== undefined) as PropertySectionItem,
    );

    this.addSelectedItemToRecent(selection);

    if (this.helpers.useMultipleSelection()) {
      // if item was not removed, add it to the selection
      this.dispatchSelectEvent({
        item: replaceByIndex(this.getArraySelection(), index, selection),
        rawItem: item,
        commit,
        autoTriggered,
      });
    } else {
      this.dispatchSelectEvent({item: selection, rawItem: item, commit, autoTriggered});
    }

    if (onItemClicked) {
      this.trackSelection({
        eventName: TrackingEvent.SelectProperty,
        props: {},
      });
    }
  }

  private removeItem(
    index,
    {
      rawItem = null,
      commit = false,
      autoTriggered = false,
    }: {rawItem?: any; commit?: boolean; autoTriggered?: boolean} = {},
  ) {
    if (this.helpers.useMultipleSelection()) {
      // if item was not removed, add it to the selection
      const selection = [...this.getArraySelection()];
      selection.splice(index, 1);

      this.dispatchSelectEvent({item: selection, rawItem, commit, autoTriggered});
    }
  }

  /**
   * Updates the SectionItem object with necessary information from the stagedItems stack.
   * TODO: Ideas to make this a bit cleaner
   *   TODO- This method currently mutates the item. While there are no current known issues, we should be more protective.
   *   TODO- Look into adding this information when and item being staged versus when it is being selected.
   */
  private applyContextFromStagedItems(item) {
    const {stagedItemsStack} = this.state;
    const stagedItemType = this.getStagedItemType();

    const [rootStagedItem, ...stagedItems] = stagedItemsStack.filter(
      (item) => !isNil(item.type), // filter out non-property items
    );
    const stagedItem = this.peekStagedItem();

    let itemValue = item;
    // TODO: this logic seems brittle. We should reconsider this design.
    if (
      ![
        StagedItemType.Object,
        StagedItemType.User,
        StagedItemType.Group,
        StagedItemType.Cohort,
        StagedItemType.BrowseProperties,
      ].includes(stagedItemType)
    ) {
      this.resetStage();
    }

    if (stagedItemType === StagedItemType.Typecast) {
      if (rootStagedItem.type === PropertyType.Object && stagedItems.length) {
        itemValue = {
          ...rootStagedItem,
          type: item.value,
          propertyDefaultType: stagedItem.type,
          joinPropertyType: rootStagedItem.type,
          propertyObjectKey: stagedItems.map((property) => property.value),
        };
      } else {
        itemValue = {
          ...stagedItem,
          type: item.value,
        };
      }
    } else if (stagedItemType === StagedItemType.Datetime) {
      itemValue = {
        ...stagedItem,
        unit: item.value,
      };
    } else if (stagedItemType === StagedItemType.Dimension) {
      itemValue = {
        ...omit(stagedItem, `dataGroupId`),
        label: stagedItem.label,
        value: item.value,
        type: item.type,
        propertyDefaultType: item.type,
        joinPropertyType: stagedItem.joinPropertyType,
        ...(stagedItem.dimensionDataGroupId && Array.isArray(item.value)
          ? {dataGroupId: stagedItem.dimensionDataGroupId}
          : {}),
        ...(stagedItem.tempDataGroupId && Array.isArray(item.value)
          ? {tempDataGroupId: stagedItem.tempDataGroupId}
          : {tempDataGroupId: undefined}),
      };
    } else if (stagedItemType === StagedItemType.Object && item.type !== PropertyType.Object) {
      this.resetStage();
      itemValue = {
        ...rootStagedItem,
        type: item.type,
        propertyObjectKey: [...stagedItems, item].map((property) => property.value),
        joinPropertyType: PropertyType.Object,
        propertyDefaultType: item.type,
      };
    }
    return itemValue;
  }

  private fetchData() {
    this.fetchPeopleProperties();
    this.fetchEventProperties();

    this.fetchDataGroups();
    this.fetchLookupTables();
    this.fetchCohorts();
    this.fetchUserDetails();
    this.fetchPropertyDefinitions();
    this.fetchProjectFeatureFlags();
  }

  private getPropertyResourceTypes() {
    return compact([
      this.attr(`project-has-people`) ? ResourceType.User : null,
      !this.attr(`hide-events`) ? ResourceType.Event : null,
    ]);
  }

  private fetchPropertyDefinitions() {
    for (const type of this.getPropertyResourceTypes()) {
      const params = {
        category: type,
        projectId: this.sourceAttrs.projectId,
        includeCustom: this.attr(`include-custom-properties`) !== IncludeCustomPropertyType.None,
        workspaceId: this.sourceAttrs.workspaceId,
      };
      PropertyDefinitionStoreMap[type].fetch(params);
    }
  }

  /**
   * TODO for perf purposes, infra team does not want standard people props list available to filter when a
   * B2B data group has been selected for the main analysis. Hopefully we can remove this restriction
   * eventually, as there are legit analysis cases that it enables.
   */
  private shouldShowUserProps() {
    return (
      (this.attr(`project-has-people`) || this.attr(`menu-item-types`).includes(MenuItemType.UserProperties)) &&
      !this.hasSelectedDataGroup()
    );
  }

  private hasSelectedDataGroup() {
    return this.attr(`all-data-groups`) && !isEmpty(this.attr(`data-group-id`));
  }

  private shouldShowCohorts() {
    return (
      this.attr(`inline-cohorts-override`)?.enable ||
      (this.attr(`project-has-cohorts`) && (!this.hasSelectedDataGroup() || this.attr(`support-b2b-cohorts`)))
    );
  }

  private getPropertyDefinitions() {
    return this.getPropertyResourceTypes().reduce((acc, type) => {
      const definitions = Object.values(PropertyDefinitionStoreMap[type].value);

      // convert data definition information about custom properties into an
      // id -> definition map, we need this map below to merge the most up-to-date
      // information from the property store with information that only exists
      // in the definition, like query counts
      const customDefinitionsMap = definitions
        .filter((definition) => definition.status === `custom`)
        .reduce((acc, def) => {
          acc[def.customPropertyId] = def;
          return acc;
        }, {});

      const inlineCustomPropertyIds = Object.keys(customPropertiesStore.value).filter((customPropertyId) => {
        return (
          !isPersistedCustomProperty(customPropertyId) &&
          !customPropertiesStore.value[customPropertyId].newCustomPropertyId
        );
      });

      const createdCustomPropertyIds = Object.keys(customPropertiesStore.value).filter(
        (customPropertyId) =>
          isPersistedCustomProperty(customPropertyId) &&
          !definitions.find((definition) => definition.customPropertyId === customPropertyId),
      );

      // add in definitions from newly created and inline custom properties
      const customDefinitions = [...createdCustomPropertyIds, ...inlineCustomPropertyIds]
        .filter((customPropertyId) => {
          const customProperty = customPropertiesStore.value[customPropertyId];
          return CUSTOM_PROPERTY_RESOURCE_TYPE_MAPPING[customProperty.resourceType] === type;
        })
        .map((customPropertyId) => {
          const customProperty = customPropertiesStore.value[customPropertyId];
          const definition = customDefinitionsMap[customPropertyId];
          return {
            ...definition,
            name: customProperty.name || definition?.name,
            description: customProperty.description || definition?.description,
            customPropertyId,
            customProperty,
            resourceType: CUSTOM_PROPERTY_RESOURCE_TYPE_MAPPING[customProperty.resourceType],
            status: `custom`,
            type: customProperty.propertyType,
          } as PropertyDefinition;
        });

      const regularDefinitions = definitions.filter((definition) => definition.status !== `custom`);

      acc[type] = [...customDefinitions, ...regularDefinitions, ...EXTRA_PROPERTY_DEFINITIONS_BY_TYPE[type]];

      return acc;
    }, {});
  }

  private getTooltipData(
    item: PropertySectionItem | Cohort | MpProperty,
    resourceType: ResourceType,
  ): ContextPanelData {
    const allowDescriptionEdit = !this.attr(`description-edit-disabled`);
    const canWriteDataDefinition =
      currentProjectStore.value?.hasPermission(Permission.WriteDataDefinitions) ||
      this.state.permissionList.includes(Permission.WriteDataDefinitions);

    let tooltipData = null;
    if (resourceType === ResourceType.Cohort) {
      tooltipData = getTooltipDataForCohort(item as Cohort, {allowDescriptionEdit});
    } else {
      const propertyDefinitions = this.getPropertyDefinitions();
      const definitions = propertyDefinitions[resourceType];
      const allowDescriptionEdit = !this.attr(`description-edit-disabled`) && canWriteDataDefinition;
      if (Object.prototype.hasOwnProperty.call(propertyDefinitions, resourceType)) {
        return getTooltipDataForProperty(item as MpProperty, definitions, {
          allowEdit: !this.attr(`hide-edit-controls`) && !this.attr(`readonly`),
          allowDescriptionEdit,
          lookupTables: lookupTablesStore.value,
        });
      }
    }
    return tooltipData;
  }

  private showContextPanel(
    item: EventSectionItem | PropertySectionItem,
    section: EventSection | PropertySection,
  ): void {
    // For some reason, in some cases (specifically, the hold property constant feature) this
    // showItemDetails function is triggered after onItemClick. The consequence is that we
    // continue showing the tooltip even after an item is selected from the menu. To work around
    // this, check if the menu is still open (it won't be open if onItemClick has already been
    // triggered), and don't show the tooltip if the menu is closed.
    if (!this.attr(`open`) || !item || item.isDisabled) {
      return;
    }

    // because of the above, the tooltip goes above the cohort filter when directly being created
    // from the cohort filter. So don't display the tooltip if we are in the specific case where
    // we have one cohort and the user attempts to edit the cohort directly from query-section
    if (this.state.modalShowing || this.state.createMenuOpen) {
      return;
    }

    let tooltipData = null;
    if (section.type === MenuItemType.Events) {
      const eventValue = (item as EventSectionItem).value;
      if (eventValue.action === ActionType.Select) {
        tooltipData = this.getContext(`eventsController`).getContextPanelData(eventValue, {
          allowEdit: !this.attr(`readonly`) && !this.attr(`hide-edit-controls`),
        });
      }
    } else {
      const propertyItem = item as PropertySectionItem;
      // show context panel data when data definitions are ready
      if (propertyItem.tooltipData) {
        tooltipData = propertyItem.tooltipData;
      } else {
        tooltipData = this.getTooltipData(propertyItem, propertyItem.resourceType);
      }
    }
    this.update({tooltipData});
  }

  private getSelectedItem() {
    const selected = this.attr(`selected`);
    return Array.isArray(selected) ? selected[0] : selected;
  }

  private getSelectedResourceType(): ResourceType {
    const selectedItem = this.getSelectedItem();
    if (!selectedItem) {
      return null;
    }
    // TODO(mack): Cohort query entries in Insights are missing resourceType pending
    // https://github.com/mixpanel/analytics/pull/27358, so assume Cohort in this case for now.
    // Remove this when not needed anymore.
    return selectedItem.resourceType || ResourceType.Cohort;
  }

  public getSelectedHoverTooltipData(): ContextPanelData {
    const selected = this.attr(`selected`);
    if (!selected || (Array.isArray(selected) && selected.length > 1)) {
      return null;
    }

    const selectedItem = this.getSelectedItem();
    const resourceType = this.getSelectedResourceType();
    const selectedItemTooltipData = selected.tooltipData || SPECIAL_PROPERTIES.get(selected?.value)?.tooltipData;

    if (selectedItemTooltipData) {
      return selectedItemTooltipData;
    } else if ([ResourceType.User, ResourceType.Event].includes(resourceType)) {
      const {customPropertyId} = selectedItem;
      const properties = resourceType === ResourceType.Event ? this.getEventProperties() : this.getUserProperties();
      const property = properties.find((p) => p.name === selectedItem.value);
      if (property && this.isPropertyDisabled(property, selectedItem.value)) {
        return null;
      }

      const mpProperty: MpProperty = {
        name: selectedItem.value,
        ...(customPropertyId ? {customPropertyId} : {}),
      };

      return this.getTooltipData(mpProperty, resourceType);
    } else if (resourceType === ResourceType.Cohort) {
      const cohortId = Number.isInteger(selectedItem.value)
        ? selectedItem.value
        : selectedItem.value.cohort.raw_cohort.unsavedId;
      const cohortItem = this.transformCohortToCohortItem(cohortsStore.value[cohortId]);
      return this.getTooltipData(cohortItem, resourceType);
    }

    return null;
  }

  public getSelectedTrackingProps(): Obj {
    const selectedItem = this.getSelectedItem();
    if (!selectedItem) {
      return null;
    }

    const props = {};
    const resourceType = this.getSelectedResourceType();
    if (resourceType === ResourceType.Cohort) {
      props[`Entity type`] = `cohort`;
      if (selectedItem.value && !Number.isInteger(selectedItem.value)) {
        props[`Is inline`] = true;
      }
    } else {
      props[`Entity type`] = `property`;
      props[`Resource type`] = resourceType;
      if (selectedItem.customPropertyId) {
        props[`Is custom`] = true;
        if (!isPersistedCustomProperty(selectedItem.customPropertyId)) {
          props[`Is inline`] = true;
        }
      }
    }
    return props;
  }

  isInlineCohort(item: PropertySectionItem) {
    return Boolean(item.value?.cohort?.raw_cohort);
  }

  handleTooltipContextAction(ev): TooltipContextActionResult {
    this.helpers.closeContextPanel();

    const {name, item} = ev.detail;
    const isCustomProperty = name === `editCustomProperty`;
    const isCohortOnFeatureFlag = name === `editCohort`;
    const isInlineCohort = this.isInlineCohort(item);

    if (isCustomProperty || isInlineCohort || isCohortOnFeatureFlag) {
      if (isCustomProperty) {
        this.clickEditCustomProperty(item.property);
      } else {
        this.editCohort(item);
      }

      ev.stopPropagation();
      return {handled: true, openedModal: true};
    } else if ([`shareCohort`, `shareCustomProperty`].includes(name)) {
      this.handleShareTooltipContextAction(ev);
    }
    return null;
  }

  handleShareTooltipContextAction(ev) {
    const {name, item} = ev.detail;
    const isShareCohort = name === `shareCohort`;

    const {eventName, props} = shareTrack.shareButtonClick(ShareButtonUISource.ContextPanel);
    this.trackEvent(eventName, props, {prefix: shareEventPrefix});
    promptSharing({
      type: isShareCohort ? SharableEntityType.Cohort : SharableEntityType.CustomProperty,
      id: isShareCohort ? item.id : item.property.customPropertyId,
      'project-id': this.attr(`project-id`),
      'is-superadmin': item.superadminOverrideShare,
    });
  }

  hasOpenedModal() {
    return false;
  }

  public getMaxRecentItemsToPersist() {
    return 15;
  }

  private getRecentlyViewedSection(
    resourceTypes: ResourceType[],
    sections: PropertySection[],
    limitPerKey = null,
  ): PropertySection {
    const allSectionsItems = sections.reduce((a, b) => [...a, ...(b.items || [])], []);

    // hide recently viewed section when searching
    if (this.getSearchFilter()) {
      return null;
    }

    return {
      label: `Recently viewed`,
      items: resourceTypes.reduce((items, resourceType) => {
        const recentItems = this.getRecentItems(resourceType, 4)
          .map((persistedItem) => {
            const persistedItemUniqueKey = getItemUniqueKey({...persistedItem, resourceType});
            const propertyItem = allSectionsItems.find((item) => {
              return getItemUniqueKey(item) === persistedItemUniqueKey;
            });
            if (!propertyItem || SPECIAL_PROPERTIES.has(propertyItem.value)) {
              return null;
            } else {
              return propertyItem;
            }
          })
          .filter(Boolean);

        if (limitPerKey) {
          recentItems.splice(limitPerKey);
        }

        return [...items, ...recentItems];
      }, []),
    };
  }

  /**
   * If `selected` = [`A`, `B`, `C`, `D`] and the properties menu items are just [`A`, `B`], unmapped selected properties are [`C`, `D`].
   */
  private getMenuSectionWithUnmappedSelected(sections) {
    const allSectionsItems = sections.reduce((a, b) => [...a, ...(b.items || [])], []);
    let selected = this.attr(`selected`) || [];
    selected = Array.isArray(selected) ? selected : [selected];

    const selectedFromThisTab = selected.filter(({resourceType, dataGroupId}) =>
      [dataGroupId, resourceType].includes(this.state.selectedTab),
    );
    const unmappedSelected = selectedFromThisTab.filter((item) => !this.isItemSelected(item.value, allSectionsItems));

    if (!unmappedSelected.length) {
      return sections;
    }
    // TODO(Gonzalo): We will use type-text icon until we have a defined way to specify the type (user, event) of the property the user selected.
    const icon = `type-text`;

    const extraItems = unmappedSelected.map((ex) => ({
      ...ex,
      isSelected: true,
      label: typeof ex.value === `string` ? ex.value : ex.label,
      icon,
    }));
    if (sections.length === 1) {
      return [
        {
          ...sections[0],
          items: [...sections[0].items, ...extraItems],
        },
      ];
    }
    return [
      ...sections,
      {
        items: extraItems,
      },
    ];
  }

  private getMenuSectionWithRecentlyViewed(
    resourceTypes: ResourceType[],
    sections: PropertySection[],
    limitPerKey = null,
  ): PropertySection[] {
    return [
      this.getRecentlyViewedSection(resourceTypes, sections, limitPerKey),
      {
        isLoading: this.state.loadingEventProperties,
        items: this.getPropertyMenuItems({
          properties: this.getCustomFormulas(
            [
              !this.attr(`hide-events`) && ResourceType.Event,
              this.attr(`project-has-people`) && ResourceType.User,
            ].filter(Boolean),
          ),
        }),
        label: `Formulas in this report`,
        showLimit: MENU_ITEM_LIMIT,
      },
      ...sections,
    ].filter(Boolean);
  }

  private getMenuSections(): PropertySection[] {
    if (this.hasStagedItem()) {
      return this.getSectionsForStagedItem(this.getStagedItemType(), this.peekStagedItem());
    } else {
      return this.getTabbedMenuSections();
    }
  }
  /**
   * Returns all sections to be rendered in the selection menu
   * based on the attribute configuration
   */
  // TODO opslead
  // eslint-disable-next-line sonarjs/cognitive-complexity
  private getTabbedMenuSections(): PropertySection[] {
    const {selectedTab} = this.state;
    const projectHasDataGroups = this.attr(`all-data-groups`);
    const projectHasEventProps = !this.attr(`hide-events`);
    const projectHasEvents = this.attr(`menu-item-types`).includes(MenuItemType.Events);
    const hasOtherOptions = this.attr(`other-options`).length > 0;
    const showRecentlyViewed = !this.attr(`hide-recently-viewed`) && !this.isNewMenu();

    let sections;

    switch (selectedTab) {
      case AllTab:
        sections = [
          ...(projectHasEvents ? this.getEventsSection() : []),
          ...(hasOtherOptions ? [this.getOtherMenuSection()] : []),
          ...(projectHasEventProps ? this.getEventsMenuSections() : []),
          ...(this.shouldShowUserProps() ? this.getUserMenuSections() : []),
          ...(projectHasDataGroups ? this.getDataGroupsSections() : []),
          ...(this.shouldShowCohorts() ? this.getCohortsMenuSections() : []),
        ];
        if (showRecentlyViewed) {
          sections = this.getMenuSectionWithRecentlyViewed(
            [ResourceType.Event, ResourceType.User, ResourceType.Other],
            sections,
            2,
          );
        }
        break;
      case AllPropertiesTab: {
        sections = this.getAllPropertiesSections();
        break;
      }
      case EventsInPropertiesMenuTab:
        sections = projectHasEvents ? this.getEventsSection() : [];
        break;
      case ResourceType.Event:
        sections = projectHasEventProps ? this.getEventsMenuSections() : [];
        if (sections.length > 0 && showRecentlyViewed) {
          sections = this.getMenuSectionWithRecentlyViewed([ResourceType.Event], sections);
        }
        break;
      case ResourceType.User:
        sections = this.shouldShowUserProps() ? this.getUserMenuSections() : [];
        if (sections.length > 0 && showRecentlyViewed) {
          sections = this.getMenuSectionWithRecentlyViewed([ResourceType.User], sections);
        }
        break;
      case ResourceType.Cohort:
        sections = this.shouldShowCohorts() ? this.getCohortsMenuSections() : [];
        break;
      default:
        sections = projectHasDataGroups ? this.getDataGroupsSections(selectedTab) : [];
    }

    if (this.helpers.useMultipleSelection()) {
      sections = this.getMenuSectionWithUnmappedSelected(sections);
    }

    if (this.state.getSortedMenuSections) {
      sections = this.state.getSortedMenuSections(sections);
    }

    if (this.hasLookupTables() && !this.getSearchFilter()) {
      // remove lookup table properties when there is no search filter (except in the "Recent" section)
      sections = this.filterLookupTablePropertiesFromSections(sections);
    }

    return sections;
  }

  private sortPropertiesByScore(properties: PropertySectionItem[]): PropertySectionItem[] {
    const maxPopularity = properties.reduce((previousValue, currentValue) => {
      return previousValue >= (Number(currentValue.uiUniqueQueryCount) || 0)
        ? previousValue
        : Number(currentValue.uiUniqueQueryCount);
    }, 1);

    const propertiesWithMeta = properties.map((item) => {
      const isEvent = item.resourceType === ResourceType.Event;
      const usage = item.isRecentlyViewed ? 2 : 0;
      const globalRelevance = (isEvent && item.densityGlobal) || 0;
      const localRelevance = (isEvent && item.densityLocal) || 0;
      const popularity = (Number(item.uiUniqueQueryCount) || 0) / maxPopularity;
      const score = usage + globalRelevance + localRelevance + popularity;
      return {item, usage, globalRelevance, localRelevance, popularity, score};
    });

    propertiesWithMeta.sort((a, b) => {
      return b.score - a.score;
    });

    if (featuresStore.value?.hasProjectFeatureFlag(ProjectFeatureFlag.Debug)) {
      propertiesWithMeta.forEach((entry) => {
        const meta = {...entry};
        delete meta.item;
        entry.item.subLabel = JSON.stringify(meta);
      });
    }

    return propertiesWithMeta.map((entry) => entry.item);
  }

  private getEventsSection() {
    return [
      ...this.getContext(`eventsController`)
        .getSections({
          includeTags: false,
          dataGroupId: this.attr(`data-group-id`),
          hideEditControls: this.attr(`hide-edit-controls`),
          selected: this.attr(`selected`),
          showAnyEvent: true,
          showSessionEvent: true,
          mainSectionShowLimit: MENU_ITEM_LIMIT,
          sortConfig: this.helpers.hasSelectSortingHeader() ? SortOptions[this.state.sortValue] : null,
          recentlyUsedEvents: this.getRecentItems(ResourceType.Event),
        })
        .map((section) => ({...section, type: MenuItemType.Events})),
    ];
  }

  private getEventProperties({
    includeHidden = false,
    onlyCustom = false,
  }: {includeHidden?: boolean; onlyCustom?: boolean} = {}) {
    let eventProperties =
      this._eventPropertySource === EventPropertySource.Store
        ? this.getEventPropertiesStore().value[this._eventPropertiesStoreKey] || []
        : this.state.eventProperties;
    if (this.showDataDefinitionsDropdowns()) {
      // we filter out custom properties here to prevent double count. custom properties from data definitions store & custom properties store are stitched together in getPropertyDefinitions
      // eventually, we want to abstract the stitching to the definitions store
      const includeSubLabel = this.showDataDefinitionsSorting();
      eventProperties = eventProperties
        .filter((prop) => (onlyCustom ? prop.status === `custom` : prop.status !== `custom`))
        .map((prop) => getDataDefinitionsPropertyMenuItem(prop, includeSubLabel));
    }
    eventProperties = this.addTempMappingsToProperties(ResourceType.Event, false, eventProperties);

    if (!includeHidden) {
      eventProperties = eventProperties.filter((prop) => !prop.hidden);
    }

    return eventProperties;
  }

  private hasLookupTables() {
    return Object.keys(lookupTablesStore.value).length !== 0;
  }

  private getLookupTableProperties(joinProperty: PropertySectionItem): PropertySectionItem[] {
    let lookupTableProperties = [];
    const {
      dimensionDataGroupId,
      tempDataGroupId,
      joinPropertyType,
      name: joinPropertyName,
      label: joinPropertyLabel,
      customPropertyId,
    } = joinProperty;
    const dataGroupId = tempDataGroupId || dimensionDataGroupId;

    const lookupProps = lookupTablesStore.value[dataGroupId]?.properties;
    if (lookupProps) {
      lookupTableProperties = lookupProps.map((lookupTableProp) => ({
        ...lookupTableProp,
        dataGroupId,
        dimensionDataGroupId: dataGroupId,
        joinPropertyType,
        joinPropertyLabel,
        subLabelTheme: SubLabelTheme.Small,
        value: [joinPropertyName, lookupTableProp.name],
        isLookupTableProperty: true,
        ...(tempDataGroupId ? {tempDataGroupId} : {}),
        ...(customPropertyId ? {customPropertyId} : {}),
      }));
    }
    return lookupTableProperties;
  }

  public getItemUniqueKey(item) {
    return getItemUniqueKey(item);
  }

  /**
   * Expands a list of properties with the properties defined in a lookup table for each mapped property in the list
   * this expansion is only done when there is a search term in the dropdown
   */
  private getAllLookupTablePropertiesForDimensionProps(properties: PropertySectionItem[]): PropertySectionItem[] {
    if (!this.hasLookupTables() || this.attr(`exclude-lookup-table-properties`)) {
      return [];
    }

    let lookupTableProperties: PropertySectionItem[] = [];
    for (const prop of properties) {
      if (prop.type === PropertyType.Dimension) {
        lookupTableProperties = [...lookupTableProperties, ...this.getLookupTableProperties(prop)];
      }
    }
    return lookupTableProperties;
  }

  private filterLookupTablePropertiesFromSections(sections: PropertySection[]) {
    return sections.map((section) => {
      if ([MenuItemType.EventProperties, MenuItemType.UserProperties].includes(section.type)) {
        section.items = section.items.filter((item) => !item.isLookupTableProperty);
        return section;
      } else {
        return section;
      }
    });
  }

  private getLookupTablePropertyFromSelected(selected: PropertySectionItem) {
    let properties = [];
    if (selected.resourceType === ResourceType.Event) {
      properties = this.getEventProperties();
    } else if (selected.resourceType === ResourceType.User) {
      properties = this.getUserProperties();
    }
    return properties.find((prop) => {
      return prop.name === selected.value[0];
    });
  }

  private getAllPropertiesSections(): PropertySection[] {
    const projectHasEventProps = !this.attr(`hide-events`);
    const projectHasDataGroups = this.attr(`all-data-groups`);
    const hasOtherOptions = this.attr(`other-options`).length > 0;
    const sectionsOfAvailableProperties = [
      ...(projectHasEventProps ? this.getEventsMenuSections() : []),
      ...(this.shouldShowUserProps() ? this.getUserMenuSections() : []),
      ...(projectHasDataGroups ? this.getDataGroupsSections() : []),
    ];
    const allItems = sectionsOfAvailableProperties.flatMap((s) => s.items);
    const sorted = this.sortProperties(allItems);
    const otherOptions = (hasOtherOptions && this.getOtherMenuSection()?.items) || [];
    const visibleItems = [...otherOptions, ...sorted.filter((item) => !item.hidden)];
    const isLoading = this.state.loadingEventProperties || this.state.loadingPeopleProperties;

    const sections: PropertySection[] = [
      {
        isLoading,
        items: visibleItems,
        showLimit: 6,
        hideTopDivider: true,
      },
    ];

    const hiddenItems = sorted.filter((item) => item.hidden);
    if (!isLoading) {
      sections.push({
        label: `Hidden Properties`,
        items: hiddenItems,
        isHidden: true,
        hideTopDivider: true,
      });
    }

    const searchStr = this.state.innerSearchFilter;
    const hasExactMatch = visibleItems.find((item) => item.label.toLowerCase() === searchStr.toLowerCase());
    const showExact = searchStr && !hasExactMatch;
    if (showExact) {
      sections.push({
        items: [
          {
            type: PropertyType.String,
            propertyDefaultType: PropertyType.String,
            label: `"${searchStr}"`,
            value: searchStr,
            resourceType: ResourceType.Event,
          },
        ],
        hideTopDivider: true,
      });
    }

    return sections;
  }

  private getEventsMenuSections(): PropertySection[] {
    const specialEventProperties = [];
    let properties = this.getEventProperties();

    if (
      this.attr(`include-custom-properties`) !== IncludeCustomPropertyType.None &&
      this._eventPropertySource !== EventPropertySource.Empty
    ) {
      // workspace filters can use inline custom properties, but cannot refer to persisted custom properties
      if (!this.attr(`enable-workspace-filter-restrictions`)) {
        properties = this.sortProperties([...properties, ...this.getCustomProperties(ResourceType.Event)]);
      }
      if (!this.attr(`hide-edit-controls`) && !this.isNewMenu()) {
        specialEventProperties.push(SPECIAL_CONTROLS.CREATE_CUSTOM_EVENT_FORMULA);
      }
    }

    if (this.attr(`exclude-dimension-properties`)) {
      properties = this.unmapPropperties(properties);
    } else {
      properties = [...properties, ...this.getAllLookupTablePropertiesForDimensionProps(properties)];
    }

    if (this.attr(`show-special-date-property`)) {
      specialEventProperties.push(DATE_PROPERTY);
    }

    if (this.attr(`show-special-dataset-property`)) {
      specialEventProperties.push(DATASET_PROPERTY);
    }

    // Surface special properties to the top
    properties = [...specialEventProperties, ...properties];

    const eventPropertiesSection = {
      type: MenuItemType.EventProperties,
      isLoading: this.state.loadingEventProperties,
      items: this.getPropertyMenuItems({properties, resourceType: ResourceType.Event}),
      label: this.attr(`hide-section-labels`) ? null : `Event properties`,
      showLimit: 0,
    };

    return [eventPropertiesSection, ...this.getHiddenEventsMenuSections()];
  }

  private getHiddenEventsMenuSections() {
    const hiddenProperties = Object.values(PropertyDefinitionStoreMap[ResourceType.Event].value).filter(
      (def) => def.hidden,
    );

    return [
      {
        type: MenuItemType.EventProperties,
        isLoading: this.state.loadingEventProperties,
        items: this.getPropertyMenuItems({properties: hiddenProperties, resourceType: ResourceType.Event}),
        label: `Hidden Event Properties`,
        showLimit: MENU_ITEM_LIMIT,
        isHidden: true,
      },
    ];
  }

  private isVisibleCustomPropertyForEvents(customProperty, eventPropertiesNameSet: Set<string>): boolean {
    const eventNames = this.eventNames();

    if (this.includeAllProperties(eventNames)) {
      return true;
    }

    // $date is a special event property which exists on all events, but is not always returned
    // by getEventProperties
    eventPropertiesNameSet.add(MPProperties.Date);

    if (isPersistedCustomProperty(customProperty.customPropertyId)) {
      const referencedEventProperties = customProperty.referencedRawEventProperties || [];
      // persisted custom properties know from the backend response all the transitively
      // referenced raw event properties
      return (
        referencedEventProperties.length === 0 ||
        Object.values(customProperty.referencedRawEventProperties).some((property: string) =>
          eventPropertiesNameSet.has(property),
        )
      );
    } else {
      const hasUserPropertiesOnly = Object.values(customProperty.composedProperties).every(
        (prop: PropertySectionItem) => prop.resourceType === ResourceType.User,
      );
      // for non-persisted custom properties we need to compute the transitively referenced
      // raw event properties by combining the referenced properties of any nested custom events
      // and any properties referenced directly by this property
      return (
        hasUserPropertiesOnly ||
        Object.values(customProperty.composedProperties).some((composedProperty: any) => {
          if (composedProperty.customPropertyId) {
            const referencedEventProperties =
              customPropertiesStore.value[composedProperty.customPropertyId].referencedRawEventProperties;
            return (
              referencedEventProperties.length === 0 ||
              referencedEventProperties.some((property: string) => eventPropertiesNameSet.has(property))
            );
          } else {
            return (
              composedProperty.resourceType === ResourceType.Event && eventPropertiesNameSet.has(composedProperty.value)
            );
          }
        })
      );
    }
  }

  private isVisibleCustomProperty(customPropertyId: string, eventPropertiesNameSet: Set<string>): boolean {
    if (!isPersistedCustomProperty(customPropertyId)) {
      return true;
    }

    const id = isPersistedCustomProperty(customPropertyId) ? parseInt(customPropertyId) : customPropertyId;
    const customProperty = customPropertiesStore.value[id];
    const property = storeCustomPropertyToProperty(customPropertyId);

    // exclude persisted behavioral props
    if (
      this.attr(`include-custom-properties`) === IncludeCustomPropertyType.PersistedNonBehavioralOnly &&
      isBehavioralCustomProperty(customProperty)
    ) {
      return false;
    }

    if (
      property.resourceType === ResourceType.Event &&
      !this.isVisibleCustomPropertyForEvents(customProperty, eventPropertiesNameSet)
    ) {
      return false;
    }

    return customProperty.isVisible || customProperty.user === this.attr(`user-id`);
  }

  private getCustomEntities(resourceTypes: ResourceType[]) {
    const eventPropertiesNameSet = new Set<string>(
      this.getEventProperties({includeHidden: true}).map((prop) => prop.name),
    );

    // TODO @jimmy: add uiUniqueQueryCount as a field in the custom properties API
    const customDefinitions = keyBy(
      this.getEventProperties({includeHidden: false, onlyCustom: true}),
      `customPropertyId`,
    );

    return Object.keys(customPropertiesStore.value)
      .filter((customPropertyId) => !customPropertiesStore.value[customPropertyId].newCustomPropertyId)
      .filter(
        (customPropertyId) =>
          this.attr(`include-custom-properties`) === IncludeCustomPropertyType.All ||
          isPersistedCustomProperty(customPropertyId),
      )
      .filter((customPropertyId) => this.isVisibleCustomProperty(customPropertyId, eventPropertiesNameSet))
      .map((customPropertyId) => {
        let property = storeCustomPropertyToProperty(customPropertyId);
        const propertyDef = customDefinitions[customPropertyId];

        property = {
          ...property,
          ...(propertyDef ? {uiUniqueQueryCount: propertyDef.uiUniqueQueryCount} : {}),
        };

        return this.getPropertyMenuItem({
          property,
          resourceType: property.resourceType,
        });
      })
      .filter((item) => resourceTypes.includes(item.resourceType));
  }

  private getCustomProperties(resourceType: ResourceType) {
    let properties = this.getCustomEntities([resourceType]).filter((item) =>
      isPersistedCustomProperty(item.customPropertyId),
    );
    if (resourceType === ResourceType.Event || resourceType === ResourceType.User) {
      properties = this.addTempMappingsToProperties(resourceType, true, properties);
    }
    return properties;
  }

  private getCustomFormulas(resourceTypes: ResourceType[]) {
    return this.getCustomEntities(resourceTypes).filter((item) => !isPersistedCustomProperty(item.customPropertyId));
  }

  private getUserProperties() {
    // return a shallow copy so that callers do not accidentally mutate the store
    let userProperties;
    if (this.showDataDefinitionsDropdowns()) {
      // we filter out custom properties here to prevent double count. custom properties from data definitions store & custom properties store are stitched together in getPropertyDefinitions
      // eventually, we want to abstract the stitching to the definitions store
      const includeSubLabel = this.showDataDefinitionsSorting();
      userProperties = Object.values(PropertyDefinitionStoreMap[ResourceType.User].value)
        .filter((prop) => prop.status !== `custom`)
        .map((prop) => getDataDefinitionsPropertyMenuItem(prop, includeSubLabel));
    } else {
      userProperties = peoplePropertiesStore.value;
    }

    userProperties = userProperties.filter((prop) => !prop.hidden);

    userProperties = this.shouldShowUserProps()
      ? this.addTempMappingsToProperties(ResourceType.User, false, userProperties)
      : [];

    return userProperties;
  }

  private isFirstEventTimeEnabled(): boolean {
    // first event time is backed by a custom behavioral property:
    // - behavioral properties are not allowed in workspace filters
    // - first event time is only valid in situations where custom properties are valid
    return (
      !this.attr(`enable-workspace-filter-restrictions`) &&
      ![IncludeCustomPropertyType.PersistedNonBehavioralOnly, IncludeCustomPropertyType.None].includes(
        this.attr(`include-custom-properties`),
      )
    );
  }

  private sortProperties(properties: PropertySectionItem[]) {
    if (this.isNewMenu()) {
      const config = SortOptions[this.state.sortValue];
      switch (config.by) {
        case SortBy.Suggested:
          return this.sortPropertiesByScore(properties);
        case SortBy.Alphabetical:
          return sortItemsByLabel(properties, {order: config.order});
      }
    } else if (this.showDataDefinitionsSorting()) {
      return sortUiUniquePropertyItems(properties);
    } else {
      return sortItemsByLabel(properties);
    }
  }

  private isBehavioralPropertiesEnabled(): boolean {
    // behavioral properties can never be used in workspace filters
    if (this.attr(`enable-workspace-filter-restrictions`)) {
      return false;
    }
    // if the containing report allows for behavioral props they are always available
    if (this.attr(`allow-behavioral-property-creation`)) {
      return true;
    }
    // otherwise feature flag determines availability (feature flag is being phased out,
    // eventually only reports with allow-behavioral-property-creation will support them)
    return featuresStore.value?.hasProjectFeatureFlag(ProjectFeatureFlag.BehavioralProperties);
  }

  private unmapPropperties(properties: any): any {
    // Make mapped properties look like they are unmapped.
    return properties.map((prop) =>
      prop.type !== `dimension`
        ? prop
        : {
            ...omit(prop, [`dimension_data_group_id`, `join_property_type`]),
            type: prop.join_property_type,
          },
    );
  }

  private getUserMenuSections(): PropertySection[] {
    let properties = this.getUserProperties();
    properties = [
      // peoplePropertiesStore properties
      ...properties,
      // special first event time property
      ...(this.isFirstEventTimeEnabled() ? [FIRST_EVENT_TIME_PROPERTY] : []),
    ];

    // workspace filters can use inline custom properites, but cannot refer to persisted custom properties
    if (
      this.attr(`include-custom-properties`) !== IncludeCustomPropertyType.None &&
      !this.attr(`enable-workspace-filter-restrictions`)
    ) {
      properties = this.sortProperties([
        ...properties,
        ...this.getCustomProperties(ResourceType.User),
        ...this.getCustomFormulas([ResourceType.User]),
      ]);
    }

    if (
      this.attr(`include-custom-properties`) !== IncludeCustomPropertyType.None &&
      !this.attr(`hide-edit-controls`) &&
      !this.isNewMenu()
    ) {
      properties = [
        ...(this.isBehavioralPropertiesEnabled() ? [SPECIAL_CONTROLS.CREATE_CUSTOM_BEHAVIORAL_PROPERTY] : []),
        SPECIAL_CONTROLS.CREATE_CUSTOM_PEOPLE_FORMULA,
        ...properties,
      ];
    }

    if (this.attr(`exclude-dimension-properties`)) {
      properties = this.unmapPropperties(properties);
    } else {
      properties = [...properties, ...this.getAllLookupTablePropertiesForDimensionProps(properties)];
    }

    const userPropertiesSection = {
      type: MenuItemType.UserProperties,
      isLoading: this.state.loadingPeopleProperties,
      items: this.getPropertyMenuItems({properties, resourceType: ResourceType.User}),
      label: this.attr(`hide-section-labels`) ? null : `Profile properties`,
      showLimit: MENU_ITEM_LIMIT,
    };

    return [userPropertiesSection, ...this.getHiddenUserMenuSections()];
  }

  private getHiddenUserMenuSections() {
    let hiddenProperties = Object.values(PropertyDefinitionStoreMap[ResourceType.User].value).filter(
      (def) => def.hidden,
    );
    /*
      TODO (duc): this is a quick patch (which hide the dim prop instead of showing it as a normal prop)
      Need to make user properties re-fecth correctly
    */
    if (this.attr(`exclude-dimension-properties`)) {
      hiddenProperties = hiddenProperties.filter((prop) => prop.type !== `dimension`);
    }
    return [
      {
        type: MenuItemType.UserProperties,
        isLoading: this.state.loadingPeopleProperties,
        items: this.getPropertyMenuItems({properties: hiddenProperties, resourceType: ResourceType.User}),
        label: `Hidden Profile Properties`,
        showLimit: MENU_ITEM_LIMIT,
        isHidden: true,
      },
    ];
  }

  private isYourCohort(cohort) {
    return cohort.createdBy && cohort.createdBy.id === this.attr(`user-id`);
  }

  private transformCohortToCohortItem(cohort: SerializedCohort | SerializedInlineCohort): CohortItem {
    if (isSerializedInlineCohort(cohort as SerializedInlineCohort)) {
      const unsavedCohort = cohort as SerializedInlineCohort;

      // Hide converted cohorts if the saved cohort exists in store
      if (
        unsavedCohort.raw_cohort.unsavedId &&
        Number.isInteger(unsavedCohort.id) &&
        cohortsStore.value[unsavedCohort.id]
      ) {
        return null;
      }

      // Unsaved cohort does not contain permissions (e.g. canView)
      return {
        label: unsavedCohort.name,
        name: unsavedCohort.name,
        value: {
          cohort: {
            ...unsavedCohort,
          },
        },
        createdBy: unsavedCohort.created_by,
      };
    }

    const savedCohort = cohort as SerializedCohort;

    return {
      id: savedCohort.id,
      label: savedCohort.name,
      value: savedCohort.id,
      name: savedCohort.name,
      count: savedCohort.count,
      description: savedCohort.description,
      dataGroupId: savedCohort.data_group_id,
      createdBy: savedCohort.created_by,
      lastEdited: savedCohort.last_edited,
      can_update_basic: savedCohort.can_update_basic,
      canView: savedCohort.can_view,
      canUpdateBasic: savedCohort.can_update_basic,
      canShare: savedCohort.can_share,
      isSuperadmin: savedCohort.is_superadmin,
      allowStaffOverride: savedCohort.allow_staff_override,
    };
  }

  private getSerializedCohorts() {
    return sortBy(cohortsStore.value, `name`)
      .filter((cohort: SerializedCohort | SerializedInlineCohort) => {
        const dataGroupId = this.attr(`data-group-id`);
        const cohortDataGroupId = isSerializedInlineCohort(cohort)
          ? cohort.raw_cohort.data_group_id
          : cohort.data_group_id;
        return dataGroupIDsEqual(dataGroupId, cohortDataGroupId ? cohortDataGroupId.toString() : ``);
      })
      .map((cohort: SerializedCohort | SerializedInlineCohort) => this.transformCohortToCohortItem(cohort))
      .filter(Boolean);
  }

  private getCohortsMenuSections(): PropertySection[] {
    const cohorts = this.getSerializedCohorts();

    let inlineCohorts;
    let [userCohorts, allCohorts] = partition(cohorts, (cohort) => this.isYourCohort(cohort));
    // @ts-ignore - TODO: Type 'PropertyItem[]' is not assignable to type 'Cohort[]'.
    [inlineCohorts, userCohorts] = partition(
      this.getPropertyMenuItems({
        properties: userCohorts,
        resourceType: ResourceType.Cohort,
      }),
      (cohort) => cohort.value && cohort.value.cohort && cohort.value.cohort.raw_cohort,
    );

    allCohorts = allCohorts.filter((cohort) => cohort.canView);
    // @ts-ignore - TODO: Type 'PropertyItem[]' is not assignable to type 'Cohort[]'.
    allCohorts = this.getPropertyMenuItems({
      properties: allCohorts,
      resourceType: ResourceType.Cohort,
    });

    // defines cohorts that were last selected and currently selected, the culprit to check if a cohort goes all the way
    // to the bottom without being categorized for some reason
    const currentlySelected = intersectionBy(this.attr(`selected`), this.state.lastMultiSelection, `value`);
    const selectedCohorts = this.getPropertyMenuItems({
      properties: cohorts,
      resourceType: ResourceType.Cohort,
      selected: currentlySelected,
    }).filter((cohort) => cohort.isSelected);

    if (this.attr(`show-all-users-cohort`)) {
      // @ts-ignore - TODO: Type 'PropertyItem[]' is not assignable to type 'Cohort[]'.
      allCohorts.unshift(ALL_USERS_COHORT);
    }

    // the `Create Cohort action can be overriden by inline-cohorts-override, regardless if
    // custom props can be created in this dropdown
    const showCreateInlineCohort =
      !this.isNewMenu() && (this.attr(`inline-cohorts-override`)?.enable ?? !this.attr(`hide-edit-controls`));

    return [
      {
        type: MenuItemType.Cohorts,
        isLoading: this.state.loadingCohorts,
        items: selectedCohorts,
        label: `Selected Cohorts`,
      },
      {
        type: MenuItemType.Cohorts,
        isLoading: this.state.loadingCohorts,
        items: [
          ...(showCreateInlineCohort ? [SPECIAL_CONTROLS.CREATE_INLINE_COHORT] : []),
          ...inlineCohorts,
          ...userCohorts,
        ],
        label: `Your Cohorts`,
      },
      {
        type: MenuItemType.Cohorts,
        isLoading: this.state.loadingCohorts,
        // @ts-ignore - TODO: Type 'CohortItem[]' is not assignable to type 'PropertyItem[]'.
        items: allCohorts,
        label: `All Cohorts`,
        showLimit: userCohorts.length > 0 ? MENU_ITEM_LIMIT : null,
      },
    ];
  }

  private getOtherMenuSection(): PropertySection {
    const items: PropertySectionItem[] = this.attr(`other-options`)
      .reduce((r, opt) => (OTHER_OPTIONS.has(opt) ? r.concat(OTHER_OPTIONS.get(opt)) : r), [])
      .map((item) => (this.isItemSelected(item.value, this.attr(`selected`)) ? {...item, isSelected: true} : item));
    return {
      isLoading: false,
      items,
      label: this.attr(`hide-section-labels`) ? null : `Other`,
    };
  }

  /**
   * Generates items-menu sections from each loaded data group
   */
  private getDataGroupsSections(dataGroupId?: string): PropertySection[] {
    const reportDataGroupId = this.attr(`data-group-id`);
    return this.getVisibleDataGroups(dataGroupId).map((group) => {
      const displayDataGroupFirstEventTime =
        this.isFirstEventTimeEnabled() && dataGroupIDsEqual(reportDataGroupId, group.groupID);
      const properties = this.sortProperties([
        // all data group properties
        ...group.properties,
        /// special
        ...(displayDataGroupFirstEventTime ? [FIRST_EVENT_TIME_PROPERTY] : []),
      ]);

      return {
        isLoading: group.isLoading,
        items: this.getPropertyMenuItems({
          dataGroupId: group.groupID,
          properties,
          resourceType: ResourceType.User,
        }),
        label: this.attr(`hide-section-labels`) ? null : `${group.displayName} Properties`,
        showLimit: MENU_ITEM_LIMIT,
      };
    });
  }

  private getVisibleDataGroups(dataGroupId?: string): DataGroupItem[] {
    const {dataGroups} = this.state;
    const singleDataGroup = this.attr(`single-data-group`);
    let groups: DataGroupItem[] = dataGroupId
      ? dataGroups[dataGroupId]
        ? [dataGroups[dataGroupId]]
        : []
      : Object.values(dataGroups);

    if (singleDataGroup) {
      groups = groups.filter((group) => group.groupID === singleDataGroup);
    }

    return groups;
  }

  /**
   * Generates all tabs to be displayed in the selection menu
   */
  private getToggleTabs() {
    const tabs = [];
    // show toggles when nothing is staged
    const canShowToggles = !this.hasStagedItem();
    if (canShowToggles) {
      const showEvents = this.attr(`menu-item-types`).includes(MenuItemType.Events);
      const showEventProperties = !this.attr(`hide-events`);
      const showUserProperties = this.shouldShowUserProps();
      const hasDataGroups = this.getVisibleDataGroups();
      const showCohorts = this.shouldShowCohorts();

      // events
      if (showEvents) {
        tabs.push({value: MenuItemType.Events, label: `Events`, disabled: false});
      }

      // properties
      if (this.isNewMenu()) {
        if (showEventProperties || showUserProperties || hasDataGroups) {
          tabs.push({value: AllPropertiesTab, label: `Properties`, disable: false});
        }
      } else {
        // event properties
        if (showEventProperties) {
          tabs.push({value: ResourceType.Event, label: `Event`, disabled: false});
        }

        // user properties
        if (showUserProperties) {
          tabs.push({value: ResourceType.User, label: `User`, disabled: false});
        }

        // data group tabs
        for (const group of this.getVisibleDataGroups()) {
          tabs.push({label: group.displayName, value: group.groupID, disabled: false});
        }
      }

      // cohorts
      if (showCohorts) {
        tabs.push({value: ResourceType.Cohort, label: `Cohorts`, disabled: false});
      }

      // TODO: Handle this for new menu
      const hasOtherOptions = this.attr(`other-options`).length > 0;
      if (!this.isNewMenu() && this.attr(`show-all-tab`) && (tabs.length > 1 || hasOtherOptions)) {
        tabs.unshift({value: AllTab, label: `All`, disabled: false});
      }
    }
    return tabs;
  }

  private eventNames(): string[] {
    return this.attr(`event-names`) && uniq<string>(this.attr(`event-names`));
  }

  private includeAllProperties(eventNames: string[]): boolean {
    // if no events are passed, or events like `$all_events` are passed, all properties should be fetched
    return (
      !eventNames ||
      eventNames.length === 0 ||
      eventNames.some((eventName) => {
        return [
          SPECIAL_EVENTS.ALL_EVENTS,
          SPECIAL_EVENTS.ANY_EVENT,
          SPECIAL_EVENTS.TOP_EVENTS,
          SPECIAL_EVENTS.ALL_MTU_EVENTS,
        ].includes(eventName);
      })
    );
  }

  /**
   * Fetches event properties for a set of events or all events
   */
  private async fetchEventProperties() {
    const eventNames = this.eventNames();
    const isPropertyIntersection = this.attr(`property-combine-mode`) === PropertyCombineMode.Intersection;
    const includeAllProperties = this.includeAllProperties(eventNames);

    this.update({loadingEventProperties: true});
    this._eventPropertiesStoreCallback = () => {
      this.update({loadingEventProperties: false});
    };

    let eventProperties = null;
    let params = null;
    let eventPropertySource;
    this._eventPropertiesStoreKey = ``;
    if (this.attr(`hide-events`) || (eventNames && eventNames.length === 0)) {
      eventProperties = [];
      eventPropertySource = EventPropertySource.Empty;
    } else if (!includeAllProperties && isPropertyIntersection) {
      let requestParamsList = [];
      let queryFcn;
      const commonParams = {
        includeDimensionProperties: !this.attr(`exclude-dimension-properties`),
        includeHidden: true,
        projectId: this.sourceAttrs.projectId,
        workspaceId: this.sourceAttrs.workspaceId,
      };
      if (this.showDataDefinitionsDropdowns()) {
        // when we fetch from data definitions, we use the propertyDefinitionsClient query instead
        requestParamsList = eventNames.map((eventName) => ({
          event: [eventName],
          ...commonParams,
        }));

        queryFcn = (requestParams) => propertyDefinitionsClient.query(requestParams);
      } else {
        requestParamsList = eventNames.map((eventName) => ({
          eventName,
          ...commonParams,
        }));

        queryFcn = (requestParams) => eventPropertiesClient.query(requestParams);
      }

      eventProperties = await Promise.all(requestParamsList.map(queryFcn));
      eventProperties = calculatePropertiesIntersection(eventProperties);
      eventPropertySource = EventPropertySource.Intersection;
    } else {
      let eventKey = ``;
      if (this.showDataDefinitionsDropdowns()) {
        eventKey = `event`;
      } else {
        eventKey = `eventName`;
      }

      params = {
        ...(includeAllProperties ? {} : {[eventKey]: eventNames}),
        includeDimensionProperties: !this.attr(`exclude-dimension-properties`),
        includeHidden: true,
        projectId: this.sourceAttrs.projectId,
        workspaceId: this.sourceAttrs.workspaceId,
      };

      this._eventPropertiesStoreKey = Store.getKey(params);

      eventPropertySource = EventPropertySource.Store;
    }

    this.getEventPropertiesStore().subscribe(this._eventPropertiesStoreCallback, this._eventPropertiesStoreKey);

    if (eventProperties) {
      this.update({eventProperties, loadingEventProperties: false});
    } else {
      this.getEventPropertiesStore().fetch(params, {}, this._eventPropertiesStoreKey);
    }
    this._eventPropertySource = eventPropertySource;
  }

  private fetchPeopleProperties() {
    this.update({loadingPeopleProperties: true});
    this._peoplePropertiesStoreCallback = () => {
      this.update({loadingPeopleProperties: false});
    };
    peoplePropertiesStore.subscribe(this._peoplePropertiesStoreCallback);
    peoplePropertiesStore.fetch({
      includeDimensionProperties: !this.attr(`exclude-dimension-properties`),
      projectId: this.attr(`project-id`),
      workspaceId: this.attr(`workspace-id`),
    });
  }

  /**
   * Updates a given dataGroup with its list of properties
   */
  private async fetchDataGroupResourceProperties(resourceType: ResourceType, dataGroupId: string) {
    let properties = [];
    const dataGroup = this.state.dataGroups[dataGroupId];
    const params = {
      dataGroupId,
      projectId: this.sourceAttrs.projectId,
      workspaceId: this.sourceAttrs.workspaceId,
    };

    try {
      if (resourceType === ResourceType.User) {
        properties = await peoplePropertiesClient.query(params);
      } else if (resourceType === ResourceType.Event) {
        properties = await eventPropertiesClient.query(params);
      } else {
        throw new Error(`Failed to fetch properties for invalid resource type ${resourceType}`);
      }
    } catch (error) {
      console.error(`Error loading data group (${dataGroupId}) user properties`, error);
    }

    this.update({
      dataGroups: {
        ...this.state.dataGroups,
        [dataGroupId]: {
          ...dataGroup,
          isLoading: false,
          properties,
        },
      },
    });
  }

  /**
   * Fetches all data groups and triggers their
   * respective properties request
   */
  private async fetchDataGroups() {
    if (!this.attr(`all-data-groups`)) {
      return this.update({dataGroups: {}});
    }
    const dataGroups = {};

    const groups = await this.retrieveDataGroups();

    for (const group of groups) {
      const dataGroupId = group.groupID;
      const cachedProperties = peoplePropertiesClient.getCachedResponse({
        dataGroupId,
        projectId: this.sourceAttrs.projectId,
        workspaceId: this.sourceAttrs.workspaceId,
      });

      dataGroups[group.groupID] = {
        ...group,
        isLoading: cachedProperties ? false : true,
        properties: cachedProperties || [],
      };
    }

    // only fetch properties for data groups that are not cached
    this.update({dataGroups});
    groups.forEach(({groupID}) => {
      if (dataGroups[groupID].isLoading) {
        this.fetchDataGroupResourceProperties(ResourceType.User, groupID);
      }
    });
  }

  private lookupTablesLoadedCallback() {
    const shouldFetchTableProperties =
      !this.attr(`exclude-lookup-table-properties`) && !this.attr(`exclude-dimension-properties`);
    if (shouldFetchTableProperties) {
      const tables = Object.values(lookupTablesStore.value);
      for (const table of tables) {
        const dataGroupId = table[`id`];
        const {projectId, workspaceId} = this.sourceAttrs;
        fetchLookupTableProperties(dataGroupId, projectId, workspaceId);
      }
    }
  }

  private fetchLookupTables() {
    this._lookupTablesStoreCallback = ({fromUpdate}) => {
      if (fromUpdate) {
        // callback triggered from manual #update call (e.g. mapping registered or unregistered)
        return;
      }
      this.registerTempLookupTableMappingFromSelected();
      this.lookupTablesLoadedCallback();
    };
    lookupTablesStore.subscribe(this._lookupTablesStoreCallback);

    if (!lookupTablesStore.hasFetchedValue()) {
      lookupTablesStore.fetch({
        ...(this.attr(`project-id`) ? {projectId: this.attr(`project-id`)} : {}),
        ...(this.attr(`workspace-id`) ? {workspaceId: this.attr(`workspace-id`)} : {}),
      });
    }
  }

  private fetchUserDetails() {
    userDetailsStore.fetch();
  }

  private fetchProjectFeatureFlags() {
    // replace with project details store once it exists
    userDetailsClient
      .query({
        projectId: this.attr(`project-id`),
        workspaceId: this.attr(`workspace-id`),
      })
      .then((userDetails) => {
        const project = userDetails.projects[this.attr(`project-id`)];
        this.update({
          featureFlags: new Set<string>(project?.feature_flags),
        });
      });
  }

  // TODO opslead
  // eslint-disable-next-line sonarjs/cognitive-complexity
  private async fetchCohorts() {
    if (!this.attr(`project-has-cohorts`)) {
      return;
    }

    this.update({loadingCohorts: true});

    // Handle unsaved -> saved conversion on store change
    this._cohortStoreCallback = () => {
      this.update({loadingCohorts: false});

      const savedCohortItems = this.getSavedCohortSelection();
      savedCohortItems.forEach((item, index) => {
        const canonicalCohort = cohortsStore.value[item.value] as SerializedCohort;
        if (item.label && canonicalCohort && canonicalCohort.name !== item.label) {
          this.replaceItem(
            {
              resourceType: ResourceType.Cohort,
              label: canonicalCohort.name,
              value: item.value,
            },
            index,
            {autoTriggered: true},
          );
        }
      });

      const inlineCohorts = this.getInlineCohortSelection();
      inlineCohorts.forEach(({value}, index) => {
        if (!value.cohort.raw_cohort.unsavedId) {
          return;
        }

        const unsavedCohort = cohortsStore.value[value.cohort.raw_cohort.unsavedId] as SerializedInlineCohort;

        if (!unsavedCohort) {
          return;
        }

        // Inline cohort converted
        if (Number.isInteger(unsavedCohort.id)) {
          const cohort = cohortsStore.value[unsavedCohort.id];

          this.replaceItem(
            {
              propertyDefaultType: PropertyType.List,
              resourceType: ResourceType.Cohort,
              label: cohort.name,
              value: {
                cohort,
              },
              createdBy: cohort.created_by,
            },
            index,
            {autoTriggered: true},
          );
        } else if (!isEqual(unsavedCohort.raw_cohort, value.cohort.raw_cohort)) {
          // Inline cohort updated
          this.replaceItem(
            {
              propertyDefaultType: PropertyType.List,
              resourceType: ResourceType.Cohort,
              label: unsavedCohort.name,
              value: {
                cohort: unsavedCohort,
              },
              createdBy: unsavedCohort.created_by,
            },
            index,
            {autoTriggered: true},
          );
        }
      });
    };

    const dataGroupId = this.attr(`data-group-id`);
    if (dataGroupId && this.attr(`support-b2b-cohorts`)) {
      cohortsStore.subscribe(this._cohortStoreCallback, dataGroupId);
      await cohortsStore.fetch(
        {
          projectId: this.attr(`project-id`),
          workspaceId: this.attr(`workspace-id`),
          dataGroupId,
        },
        {},
        dataGroupId,
      );
    } else {
      cohortsStore.subscribe(this._cohortStoreCallback);
      await cohortsStore.fetch({
        projectId: this.attr(`project-id`),
        workspaceId: this.attr(`workspace-id`),
      });
    }
  }

  private getPropertyMenuItems({
    dataGroupId,
    properties,
    resourceType,
    selected,
    propertyDefaultType,
  }: {
    dataGroupId?: string;
    properties?: any[];
    resourceType?: ResourceType;
    selected?: any;
    propertyDefaultType?: PropertyType;
  } = {}): PropertySectionItem[] {
    const items = properties.map((property) =>
      this.getPropertyMenuItem({dataGroupId, property, resourceType, selected, propertyDefaultType}),
    );

    return items;
  }

  private isPropertyDisabled(property, propertyValue) {
    if (property?.customPropertyId && !isPersistedCustomProperty(property?.customPropertyId)) {
      return false;
    }

    return Boolean(
      Object.prototype.hasOwnProperty.call(this.attr(`disabled-items`), propertyValue) ||
        (!this.attr(`selectable-sensitive-properties`) && !this.isPropertyAccessible(property)),
    );
  }

  private isPropertyAccessible(property) {
    return currentProjectStore.value?.hasSensitiveAccess || !property.sensitive;
  }

  // TODO opslead
  // eslint-disable-next-line sonarjs/cognitive-complexity
  private getPropertyMenuItem({
    dataGroupId,
    property,
    resourceType,
    selected,
    propertyDefaultType,
  }: {
    dataGroupId?: string;
    property?: PropertySectionItem;
    resourceType?: ResourceType;
    selected?: any;
    propertyDefaultType?: PropertyType;
  } = {}): PropertySectionItem {
    const forCohorts = resourceType === ResourceType.Cohort;
    const dataDefinition = eventPropertyDefinitionsStore.value[property.name];

    if (property.resourceType) {
      // custom properties/formulas specify their own resource types
      resourceType = property.resourceType;
    }

    selected = selected || this.attr(`selected`);

    const {customPropertyId, customProperty, isControlItem, subLabelTheme} = property;
    let {label, name} = property;
    const value = property.value || name || (customProperty && customProperty.name);
    label = label || value;
    name = name || value;

    const customPropertyIdMatches = selected?.customPropertyId && selected.customPropertyId === customPropertyId;
    const dimensionDataGroupIdMatches =
      (selected?.dataGroupId && selected.dataGroupId === property.dimensionDataGroupId) ||
      (selected?.tempDataGroupId && selected.tempDataGroupId === property.dimensionDataGroupId);
    const dimensionPropertyMatches =
      dimensionDataGroupIdMatches &&
      (!selected?.customPropertyId || customPropertyIdMatches) &&
      [selected.value, property.value].every(Array.isArray) &&
      selected.value?.[0] === property.value?.[0] &&
      selected.value?.[1] === property.value?.[1];
    const bothAreJoinKeys =
      selected?.joinPropertyType &&
      property.joinPropertyType &&
      !Array.isArray(selected.value) &&
      // property.value is currently just omitted for join keys, but testing if it's an array in case that changes
      !Array.isArray(property.value);
    // selected.dataGroupId is currently omitted for join keys; joinPropertyType is the only way to tell it's a dimension prop
    const noDimensionDataGroup = !selected?.joinPropertyType && !property.joinPropertyType;
    const isSelected =
      (forCohorts && this.isItemSelected(value, selected)) ||
      (Array.isArray(selected) && this.isItemSelected(value, selected)) ||
      (selected && dataGroupId && dataGroupId === selected.dataGroupId && selected.value === value) ||
      dimensionPropertyMatches ||
      (selected &&
        (noDimensionDataGroup || bothAreJoinKeys) &&
        selected.value === value &&
        selected.resourceType === resourceType) ||
      (noDimensionDataGroup && (noDimensionDataGroup || bothAreJoinKeys) && customPropertyIdMatches) ||
      false;
    const appliedDataGroupId = property.dataGroupId ? property.dataGroupId : dataGroupId;

    const type = this.getPropertyType(name, property, isSelected && selected);
    const propertyType = propertyDefaultType || (isSelected && selected.propertyDefaultType) || property.type;
    const isDisabled = this.isPropertyDisabled(property, value);
    const isAccessible = this.isPropertyAccessible(property);
    const tooltip = !isAccessible ? {text: DISABLED_DUE_TO_CLASSIFIED_DATA} : null;
    const isDimension = type === PropertyType.Dimension;
    const includeLookupTableProperties = this.canShowLookupTablePropertyMenu({type});

    const recentlyUsedUniqueKeys = new Set(
      [ResourceType.Event, ResourceType.User].flatMap((resourceType) => {
        return this.getRecentItems(resourceType).map((item) => {
          return getItemUniqueKey({...item, resourceType});
        });
      }),
    );

    let allowPill = this.attr(`allow-typecast-pill-action`) === TypecastPillAction.Typecast;
    if (this.attr(`allow-typecast-pill-action`) === TypecastPillAction.TriggerTutorial) {
      allowPill = !this.isNewMenu();
    }
    const canTypecastProperty = !forCohorts && !isDimension && !isControlItem && isAccessible;

    const propertyItem: PropertySectionItem = {
      ...(appliedDataGroupId ? {dataGroupId: appliedDataGroupId} : {}),
      ...(customPropertyId ? {customPropertyId} : {}),
      ...(customProperty ? {customProperty} : {}),
      ...(property.tempDataGroupId ? {tempDataGroupId: property.tempDataGroupId} : {}),
      ...(property.dimensionDataGroupId ? {dimensionDataGroupId: property.dimensionDataGroupId} : {}),
      ...(forCohorts ? property : {}),
      ...(property.tooltipData ? {tooltipData: property.tooltipData} : {}),
      hasCaret:
        (includeLookupTableProperties || property.type === PropertyType.Object || this.attr(`show-items-caret`)) &&
        !isControlItem &&
        isAccessible,
      persistCaret: this.isNewMenu(),
      hasPill: allowPill && canTypecastProperty,
      hidden: !!property.hidden,
      icon: property.icon || getIconForPropertyType(type),
      isControlItem,
      isDisabled,
      tooltip,
      isSelected,
      joinPropertyType: property.joinPropertyType,
      label,
      name,
      pillAction: PropertyItemAction.Typecast,
      pillLabel: `Typecast`,
      propertyDefaultType: propertyType,
      resourceType,
      type,
      value,
      densityLocal: property.densityLocal,
      densityGlobal: property.densityGlobal,
      count: property.count,
      uiQueryCount: property.uiQueryCount,
      uiUniqueQueryCount: property.uiUniqueQueryCount,
      ...(property.joinPropertyLabel ? {subLabel: property.joinPropertyLabel} : {subLabel: property?.subLabel}),
      isLookupTableProperty: property.isLookupTableProperty,
      ...(subLabelTheme ? {subLabelTheme} : {}),
      searchableContent: [dataDefinition?.description, dataDefinition?.name].join(` `),
    };

    if (this.isNewMenu()) {
      const getEntityTypeLabel = () => {
        switch (propertyItem.resourceType) {
          case ResourceType.User:
            if (propertyItem.dataGroupId) {
              const dataGroup = this.state.dataGroups[propertyItem.dataGroupId];
              if (dataGroup) {
                return dataGroup.displayName;
              }
            }
            return `User`;
          case ResourceType.Event:
          case ResourceType.Cohort:
          case ResourceType.Other:
          case null:
            return ``;
          default:
            assertUnreachable(propertyItem.resourceType);
        }
      };
      const entityTypeLabel = getEntityTypeLabel();
      // subLabel is already set to resourceType for some items (custom properties, custom behaviors, etc).
      const subLabelPrefix = entityTypeLabel !== propertyItem.subLabel ? entityTypeLabel : null;
      propertyItem.icon = null;
      propertyItem.subLabel = [subLabelPrefix, propertyItem.subLabel].filter(Boolean).join(`.`);
      propertyItem.subLabelTheme = SubLabelTheme.Breadcrumb;
    }
    propertyItem.isRecentlyViewed = recentlyUsedUniqueKeys.has(getItemUniqueKey(propertyItem));
    return propertyItem;
  }

  public getSearchFilter(): string {
    let searchFilter = ``;
    if (this.isContentSearchable()) {
      searchFilter = this.state.searchFilter || this.attr(`search-filter`);
    } else {
      searchFilter = this.state.innerSearchFilter;
    }
    return searchFilter;
  }

  private getPropertyType(name: string, property: PropertySectionItem, selected?: PropertySectionItem): PropertyType {
    const type = property.type;

    if (selected.joinPropertyType === PropertyType.Object) {
      return PropertyType.Object;
    }

    if (selected.type && type !== PropertyType.Dimension) {
      return selected.type;
    }

    return STRING_TYPECAST_PROPERTIES.includes(name) ? PropertyType.String : type;
  }

  private getArraySelection() {
    const selected = this.attr(`selected`);

    if (Array.isArray(selected)) {
      return selected;
    }

    return [];
  }

  // Simple helper to make more obvious what is happening and to make code trail easier to follow
  private canShowLookupTablePropertyMenu({type, selected}: {type?: PropertyType; selected?: any}): boolean {
    if (this.attr(`exclude-lookup-table-properties`)) {
      return false;
    }

    return type === PropertyType.Dimension || this.isDimensionSelected(selected);
  }

  private isDimensionSelected(selected) {
    if (!selected) {
      return false;
    }
    // lookup table (aka dimension property) selected.value is an array of two strings (vs b2b profiles)
    return (selected.dataGroupId || selected.tempDataGroupId) && Array.isArray(selected.value);
  }

  private isItemSelected(value, selection: PropertySectionItem[] | PropertySectionItem) {
    const getCompareValue = (v) => (v?.cohort?.raw_cohort ? v.cohort.raw_cohort.unsavedId : v);

    if (Array.isArray(selection)) {
      return selection.some((item) => getCompareValue(item.value) === getCompareValue(value));
    }

    return selection && getCompareValue(selection.value) === getCompareValue(value);
  }

  private async editCohort(item) {
    const cohortParsed = item.id ? cohortsStore.value[item.id] : item.value?.cohort?.raw_cohort || item.value?.cohort;
    const cohort = CohortModel.parseCohort(cohortParsed);
    const {cohortStagedValue, cohortSavedValue} = await this.promptInlineCohortsModal(cohort);

    this.handleCohortUpdate(cohortStagedValue, cohortSavedValue);
    if (cohortStagedValue?.cohort || cohortSavedValue?.cohort) {
      this.trackEvent(TrackingEvent.OpenInlineCohortsModal, {
        action: `Edit`,
        context: this._context,
      });
    }
  }

  /**
   * Adding a temporary cohort, editing a temporary cohort, saving a new cohort directly,
   * or saving a temporary cohort
   * @param {object} [options]
   * @param {number} [options.id] - id of the cohort, only applicable if saving the cohort
   * @param {CohortModel} [options.cohort] - cohort being passed in, only exists if not saving the cohort
   * @param {string} [options.name] - name of the cohort
   * @param {boolean} [options.savedAsNew] - whether we are saving the cohort as new
   *
   * Adding a new temporary cohort - name, cohort
   * []Editing a temporary cohort - name, cohort
   * []Saving an existing temporary cohort - id, name, cohort
   * Saving a new cohort / editing an existing cohort - id, name, cohort, savedAsNew = false
   * Saving an existing cohort as new - id, name, cohort, savedAsNew = true
   */
  private commitCohort({id, cohort, name, savedAsNew}: InlineCohortsModalCommitEvent['detail']) {
    let itemCohort: SerializedCohort | SerializedInlineCohort;
    const isSaved = Boolean(id);
    const isEditingTemporaryCohort = Boolean(cohort.unsavedId);
    /**
     * cohort.unsavedId
     * "" - saving a new cohort
     * null - updating or saving an existing cohort as new
     * a string - editing or saving an existing temporary cohort
     */
    const unsavedId = cohort.unsavedId || cuid();

    // applies to an existing and temporary cohort, both are considered saved states
    if (isSaved) {
      const item = this.getCohortPropertyItem(name, this.getCreatedBy(), id);
      this.replaceOrSelectCohort(item, isEditingTemporaryCohort || isSaved, unsavedId);
    } else {
      itemCohort = {
        name,
        raw_cohort: {
          ...cohort.serialize(),
          unsavedId,
          ...(userDetailsStore.value
            ? {
                createdBy: {
                  id: this.attr(`user-id`),
                  name: userDetailsStore.value.name,
                  email: userDetailsStore.value.email,
                },
              }
            : {}),
        },
      } as SerializedInlineCohort;
      const item = this.getCohortPropertyItem(name, this.getCreatedBy(), {cohort: itemCohort});
      this.replaceOrSelectCohort(item, isEditingTemporaryCohort, unsavedId);
    }

    if (!isSaved) {
      // updating cohort store for cohorts that are temporary
      const unsavedCohort = itemCohort as SerializedInlineCohort;
      cohortsStore.update({
        [unsavedId]: {
          name,
          raw_cohort: unsavedCohort.raw_cohort,
          created_by: cohort.createdBy,
        } as SerializedInlineCohort,
      });
    } else if (isEditingTemporaryCohort && isSaved) {
      // Mark unsaved cohort being converted to a saved cohort
      cohortsStore.update({
        [unsavedId]: {
          name,
          created_by: cohort.createdBy,
          ...(cohortsStore.value[unsavedId] || {}),
          id: cohort.id,
        } as SerializedInlineCohort,
      });
    }
    const isEditingExistingCohort = cohort.unsavedId === null && savedAsNew;
    this.trackInlineCohorts(name, isSaved, isEditingTemporaryCohort, isEditingExistingCohort, savedAsNew);
  }

  private trackInlineCohorts(name, isSaved, isEditingTemporaryCohort, isEditingExistingCohort, savedAsNew) {
    if (isSaved) {
      this.trackEvent(TrackingEvent.SaveInlineCohorts, {
        context: this._context,
        name,
        // Indicates whether an applied inline cohort was later saved
        converted: !!isEditingTemporaryCohort,
        savedAsNew,
      });
    } else if (!isEditingTemporaryCohort) {
      this.trackEvent(TrackingEvent.CreateInlineCohort, {
        context: this._context,
        name,
      });
    } else {
      this.trackEvent(TrackingEvent.EditInlineCohorts, {
        context: this._context,
        name,
        existing: isEditingExistingCohort,
      });
    }
  }

  // add cohort to the selected attr, either replace an existing selected cohort, or add a new cohort
  // to the selected properties menu
  private replaceOrSelectCohort(item: PropertySectionItem, isEditingCohort: boolean, unsavedId: string) {
    const selected = this.attr(`selected`);

    // based on internal user feedback cohorts created inline
    // should be committed much like custom props with breakdowns
    const index = Array.isArray(selected)
      ? this.getArraySelection().findIndex(
          (selectedItem) =>
            selectedItem.value.cohort?.raw_cohort?.unsavedId === unsavedId || selectedItem.value === item.value,
        )
      : isEqual(selected?.value, item.value);

    if (isEditingCohort && selected && index > -1) {
      this.replaceItem(item, index, {commit: true});
    } else {
      this.selectItem(item, {commit: true});
    }
  }

  private getCreatedBy(): User {
    return {
      id: this.attr(`user-id`),
      name: userDetailsStore?.value.name,
      email: userDetailsStore?.value.email,
    };
  }

  private getCohortPropertyItem(name: string, createdBy: User, value: TODO) {
    return {
      type: PropertyType.List,
      propertyDefaultType: PropertyType.List,
      resourceType: ResourceType.Cohort,
      label: name,
      value,
      createdBy,
    } as PropertySectionItem;
  }

  private getSavedCohortSelection(): SavedCohortItem[] {
    const selected = this.attr(`selected`);
    const cohorts = Array.isArray(selected) ? selected : [selected];

    return cohorts.filter((item) => item?.resourceType === ResourceType.Cohort && Number.isInteger(item?.value));
  }

  private getInlineCohortSelection() {
    const selected = this.attr(`selected`);
    const cohorts = Array.isArray(selected) ? selected : [selected];

    return cohorts.filter((item) => item && item.value && item.value.cohort && item.value.cohort.raw_cohort);
  }

  private getInlineCohortItems() {
    return this.getInlineCohortSelection().map((item) => ({
      ...item,
      createdBy: item.value.cohort.raw_cohort.createdBy,
      resourceType: ResourceType.Cohort,
    }));
  }

  private getSelectedCustomFormula(resourceType) {
    let selected = this.attr(`selected`) ? this.attr(`selected`) : [];
    if (!Array.isArray(selected)) {
      selected = [selected];
    }

    return selected.filter(
      (item) =>
        item.resourceType === resourceType &&
        item.customPropertyId &&
        !isPersistedCustomProperty(item.customPropertyId),
    );
  }

  private registerTempLookupTableMappingFromSelected() {
    const selected = this.attr(`selected`);
    const tempDataGroupId = selected?.tempDataGroupId;
    if (tempDataGroupId) {
      registerTempLookupTableMapping(selected, tempDataGroupId);
    }
  }

  private addTempMappingsToProperties(
    resourceType: ResourceType.Event | ResourceType.User,
    custom: boolean,
    storeProperties,
  ) {
    const mappings = getTempLookupTableMappings(resourceType, custom);
    return storeProperties.map((storeProperty) => {
      // storeProperty is of type PropertyItem ('name' field used, 'value' field not used for name),
      // but the store is typed as Record<string,any>
      const value = custom ? storeProperty.customPropertyId : storeProperty.name;
      if (mappings.has(value)) {
        const tempDataGroupId = mappings.get(value);
        const joinPropertyType = storeProperty.joinPropertyType || storeProperty.type;
        return {...storeProperty, tempDataGroupId, type: PropertyType.Dimension, joinPropertyType};
      } else {
        return storeProperty;
      }
    });
  }

  public isNewMenu(): boolean {
    return (
      Boolean(featuresStore.value?.hasProjectFeatureFlag(ProjectFeatureFlag.NewDropdowns)) &&
      !this.attr(`force-old-dropdowns`)
    );
  }

  private getSectionsForStagedItem(stagedItemType: StagedItemType, item): PropertySection[] {
    switch (stagedItemType) {
      case StagedItemType.Typecast:
        return this.helpers.getTypecastMenuSections(item) as PropertySection[];
      case StagedItemType.Datetime:
        return this.helpers.getDatetimeMenuSections() as PropertySection[];
      case StagedItemType.Dimension:
        return this.helpers.getDimensionMenuSection(item);
      case StagedItemType.Object:
        return this.helpers.getObjectMenuSection();
      case StagedItemType.User:
        return this.getUserMenuSections();
      case StagedItemType.Group:
        return this.helpers.getGroupMenuSection(item);
      case StagedItemType.Cohort:
        return this.helpers.getCohortMenuSection();
      case StagedItemType.BrowseProperties:
        return this.getTabbedMenuSections();
      default:
        assertUnreachable(stagedItemType);
    }
  }

  async createCustomProperty(resourceType: ResourceType, forBehavioralProperty: boolean) {
    const props = {resourceType};
    const {appliedValue, deletedId, didClose} = await this.promptCustomPropertiesModal(props, forBehavioralProperty);
    if (appliedValue) {
      this.helpers.applyCustomProperty(appliedValue, null);
    } else if (didClose) {
      this.helpers.closeModal();
    } else if (deletedId) {
      this.helpers.deletedCustomProperty();
    }
    this.trackEvent(TrackingEvent.CreateCustomProperty, {
      context: this._context,
      name: appliedValue?.name,
      ...props,
    });
  }

  async createInlineCohort() {
    this.trackEvent(TrackingEvent.OpenInlineCohortsModal, {
      action: `Create`,
      context: this._context,
    });
    const newDefaultCohort = new CohortModel({...new CohortModel(), dataGroupId: this.attr(`data-group-id`)});
    const {cohortStagedValue, cohortSavedValue} = await this.promptInlineCohortsModal(newDefaultCohort);
    this.helpers.closeModal();
    this.handleCohortUpdate(cohortStagedValue, cohortSavedValue);
  }

  set context(value) {
    this._context = value;
  }

  set getSortedMenuSections(getSortedMenuSections) {
    this.update({getSortedMenuSections});
  }
  private dispatchSelectEvent(selectDetails: SelectedPropertyItem): void {
    const selectEvent: SelectPropertyEvent = new CustomEvent(`select`, {detail: selectDetails});
    this.dispatchEvent(selectEvent);
  }

  /**
   * This determines if a stagedItemType should be staged as a menu or treated as a selectable item.
   * This logic should be dependent on the context of the menu and not the item itself.
   */
  private canStageItemType(stagedItemType: StagedItemType): boolean {
    switch (stagedItemType) {
      case StagedItemType.Datetime:
        return this.attr(`show-special-date-property`) && !this.attr(`hide-special-date-property-unit`);
      case StagedItemType.Typecast:
        return this.attr(`allow-typecast-pill-action`) === TypecastPillAction.Typecast;
      case StagedItemType.Dimension:
        return !this.attr(`exclude-lookup-table-properties`);
      default:
        return true;
    }
  }
}

export const PropertiesMenuEl = defineAndMakeJsxFactory<PropertiesMenuJsxProps>(`mp-properties-menu`, PropertiesMenu);
